import React, {
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
  useMemo,
  useState,
} from "react";
import { Box, CircularProgress, Typography, Button, Skeleton } from "@mui/material";
import { useSelector, useDispatch, useStore } from "react-redux";
import {
  selectMessages,
  selectInboxMessageLoading,
  selectSelectedContactID,
  selectUnreadIncomingMessages,
  selectContactExistsInInbox,
  selectInboxRecentData,
} from "../../../redux/selectors";
import ChatCard from "./ChatCard";
import ChatLog from "./ChatLog";
import { formatChatTime, formatDateSeparator } from "../../../utils/timestampUtils";
import {
  fetchMessagesByContact,
  fetchOlderMessages,
  fetchMessagesAroundMessage,
  clearMessages,
  markMessageAsRead,
  setExpectedContact,
} from "../../../redux/Slice/Inbox/InboxMessageSlice";
import { clearContactInfo, fetchContactById } from "../../../redux/Slice/Inbox/inboxInfoSlice";
import { claimMarkAsReadDispatch } from "../../../utils/markReadDedupe";
import { setselectedContactID } from "../../../redux/Slice/Inbox/inboxAllSlice";
import { clearUnreadCount } from "../../../redux/Slice/Inbox/inboxRecentSlice";
import { setActiveChat, clearActiveChat } from "../../../utils/socketHandler";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { setSelectedOption } from "../../../redux/Slice/Settings/settingsSlice";
import { fetchTeamMemberChatMessages, fetchOlderTeamMemberMessages, markTeamMemberChatAsRead } from "../../../redux/Slice/Inbox/teamMemberChatSlice";
import {
  ProfileTick,
  ProfileRemove,
  TickCircle,
  Call as CallIcon,
  CallSlash,
  CallRemove,
  CallOutgoing,
  CallIncoming,
  Forbidden2,
  Unlock,
  Edit2,
  People,
} from "iconsax-react";
import NoChannelIllustration from "../../../Images/Inbox/no-channel-connected.svg";
import { resolveDisplaySender } from "../../../utils/resolveDisplaySender";

// Inserts date separators; each message (including image/video) stays its own row — no consecutive media grouping.
const getMessageTimestamp = (msg) => {
  if (!msg || typeof msg !== 'object') return null;
  return msg.time || msg.timestamp || msg.createdAt || null;
};

const groupConsecutiveMessages = (messages) => {
  const groupedMessages = [];
  let lastMessageDate = null;
  let lastWasDateSeparator = false;

  messages.forEach((msg) => {
    if (msg.type === "date") {
      const messageTimestamp = getMessageTimestamp(msg);
      const messageDate = messageTimestamp ? new Date(messageTimestamp).toDateString() : null;
      if (messageDate && messageDate !== lastMessageDate) {
        groupedMessages.push({
          type: "date",
          timestamp: messageTimestamp,
          date: messageDate,
        });
        lastMessageDate = messageDate;
        lastWasDateSeparator = true;
      }
      return;
    }
    
    const messageTimestamp = getMessageTimestamp(msg);
    const messageDate = messageTimestamp ? new Date(messageTimestamp).toDateString() : null;
    
    if (messageDate && messageDate !== lastMessageDate && !lastWasDateSeparator) {
      groupedMessages.push({
        type: "date",
        timestamp: messageTimestamp,
        date: messageDate,
      });
      lastMessageDate = messageDate;
      lastWasDateSeparator = true;
    } else {
      lastWasDateSeparator = false;
    }

      groupedMessages.push(msg);
  });

  return groupedMessages;
};

// Bolds just the dynamic bit (a name) inside a system-message string, leaving
// the surrounding sentence at normal weight (Rahim, 2026-08-24: "dont make
// the icons bold only make the things in the text bold liek name etc").
const Emph = ({ children }) => (
  <Box component="span" sx={{ fontWeight: 600 }}>
    {children}
  </Box>
);

// Platform shown on a chat bubble's avatar badge: a message with to or from === "whatsapp_web"
// (or platform "whatsapp_web") came from the WhatsApp Webchat -> black icon.
const getMessageBadgePlatform = (msg, conversationPlatform) =>
  msg?.to === "whatsapp_web" || msg?.from === "whatsapp_web" || msg?.platform === "whatsapp_web"
    ? "whatsapp_web"
    : msg?.platform || conversationPlatform || "whatsapp";

const ChatBody = ({ contactId: propContactId,isTeamMemberChat = false, teamMemberChatData = null }) => {
  const dispatch = useDispatch();
  const store = useStore();
  // Live-store check: has a chat selection just been started elsewhere (MessageCard's URL->selectContact
  // dispatches selectedContactID + expectedContactId in the same commit, before this component's effects
  // run, but after the render whose `contactId` they close over)? The two "no contact selected ->
  // clearMessages" effects below must not wipe that: clearMessages resets expectedContactId, so the
  // in-flight response was then dropped by the late-response guard and the chat never opened.
  const selectionInProgress = () => {
    const live = store.getState();
    return !!live.inboxAll?.selectedContactID || live.inboxMessage?.expectedContactId != null;
  };
  const navigate = useNavigate();
  const { accountid } = useParams();
  // Use memoized selectors to prevent unnecessary re-renders
  const messages = useSelector(selectMessages);
  const loading = useSelector(selectInboxMessageLoading);
  const selectedContactID = useSelector(selectSelectedContactID);
  // expectedContactId is set by setExpectedContact() BEFORE the fetch, so it is
  // non-null during the entire fetch even when selectedContactID is still null/old.
  const expectedContactId = useSelector((state) => state.inboxMessage?.expectedContactId);
  const unreadIncomingMessages = useSelector(selectUnreadIncomingMessages);
  
  // Get team member chat data if in team member chat mode
  const teamMemberChat = useSelector((state) => state.teamMemberChat?.currentChat);
  const teamMemberMessages = useMemo(
    () => (isTeamMemberChat ? (teamMemberChat?.messages || []) : []),
    [isTeamMemberChat, teamMemberChat]
  );
  const teamMemberLoading = isTeamMemberChat ? (teamMemberChat?.loading || false) : false;
  const teamMemberError = isTeamMemberChat ? (teamMemberChat?.error || null) : null;
  
  // Get user data from Redux state (not localStorage for security)
  const user = useSelector((state) => state.auth?.user);
  const location = useLocation();
  /** Bumps when the tab becomes visible again so mark-as-read can re-run after visibility guard. */
  const [docVisibilityEpoch, setDocVisibilityEpoch] = useState(0);
  // Same signal Dashboard.jsx uses to decide whether any channel is connected -
  // "Connect Channel" CTA below only makes sense while none is.
  const currentWorkspace = useSelector((state) => state.workspace?.currentWorkspace);
  // whatsappConnected is Meta-only -- a QR-connected workspace is checked separately via
  // whatsappConnectionType (see Dashboard.jsx's own copy of this same comment).
  const isChannelConnected = Boolean(
    currentWorkspace?.whatsappConnected ||
      currentWorkspace?.whatsappConnectionType === "baileys" ||
      currentWorkspace?.instagramConnected ||
      currentWorkspace?.facebookConnected ||
      currentWorkspace?.websiteConnected
  );
  const messageRefs = useRef({});
  const chatContainerRef = useRef(null);

  // Ref to keep track of previous scrollHeight before fetching older messages
  const prevScrollHeightRef = useRef(0);
  // Ref to flag that we're fetching older messages
  const isFetchingOlderRef = useRef(false);
  // State to track whether scrolling should be enabled
  const [scrollLoadingEnabled, setScrollLoadingEnabled] = useState(false);
  // Ref to track initial loading
  const initialLoadCompleteRef = useRef(false);
  // Ref to track if we've fetched messages for the current team member chat
  const hasFetchedTeamMemberRef = useRef(null);
  // State for loading older messages
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  // State for loading targeted message
  const [isLoadingTargetMessage, setIsLoadingTargetMessage] = useState(false);
  const [, setTargetMessageId] = useState(null);
  /** When true, skip auto scroll-to-bottom so reply-jump / historical window is not overwritten. */
  const suppressAutoScrollToBottomRef = useRef(false);

  // Add a ref to track if we've already marked messages as read for this conversation
  const messageMarkedRef = useRef({});

  // Ref used by the contact-change effect to read current messages state
  // without adding it to the effect's dependency array.
  const messagesForGuardRef = useRef(messages);
  messagesForGuardRef.current = messages;

  
  // Loading timeout to prevent getting stuck
  const loadingTimeoutRef = useRef(null);
  const [, setIsLoadingTimeout] = useState(false);
  
  // Track which contact is currently being loaded to prevent visual artifacts.
  // Stored as a ref (not state) so changes never cause dependency-array churn
  // in selectContactUnified — stale-closure bugs were the root cause of dead clicks.
  const loadingContactIdRef = useRef(null);
  const [, setLoadingContactId] = useState(null);
  const selectUnifiedInFlightRef = useRef(null);

  // Function to clear all local states and refs (defined first to avoid hoisting issues)
  const clearLocalStates = useCallback(() => {
    // Clear message refs
    messageRefs.current = {};

    // Reset all refs to initial state
    isFetchingOlderRef.current = false;
    initialLoadCompleteRef.current = false;
    prevScrollHeightRef.current = 0;

    // Clear read message tracking
    messageMarkedRef.current = {};

    // Reset scroll loading state
    setScrollLoadingEnabled(false);
    
    // Clear loading timeout
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
    }
    setIsLoadingTimeout(false);
    
    // Clear loading contact ID (both ref and state)
    loadingContactIdRef.current = null;
    setLoadingContactId(null);
    
    // Clear pagination loading states
    setIsLoadingOlder(false);
    setIsLoadingTargetMessage(false);
    setTargetMessageId(null);
    suppressAutoScrollToBottomRef.current = false;

    // Reset scroll position if container exists
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = 0;
    }

  }, []);

  // Function to clear all Redux states for previous chat
  const clearAllChatStates = useCallback(() => {
    // Clear all Redux states related to the previous chat
    dispatch(clearMessages());        // Clear message history
    dispatch(clearContactInfo());     // Clear contact info
    clearActiveChat();                // Clear active chat in socket (not a Redux action)

    // Note: Don't clear selectedContactID here as we need it for the new chat
  }, [dispatch]);

  // Stable refs for the unmount-only cleanup effect so the empty-deps closure
  // always calls the latest version of these callbacks.
  const clearAllChatStatesRef = useRef(clearAllChatStates);
  const clearLocalStatesRef = useRef(clearLocalStates);
  clearAllChatStatesRef.current = clearAllChatStates;
  clearLocalStatesRef.current = clearLocalStates;

  // Unified function to select a contact and sync all related states.
  // IMPORTANT: loadingContactId intentionally excluded from deps — we read it via
  // loadingContactIdRef to avoid creating a new callback reference on every loading
  // state change, which was causing the "dead click" bug where selectUnifiedInFlightRef
  // blocked re-entry for a contact that had never actually been loaded.
  const selectContactUnified = useCallback(async (newContactId) => {
    if (!newContactId) {
      return;
    }
    if (selectUnifiedInFlightRef.current === newContactId) {
      return;
    }
    selectUnifiedInFlightRef.current = newContactId;

    // Step 1: Set loading state immediately (both ref and state)
    loadingContactIdRef.current = newContactId;
    setLoadingContactId(newContactId);

    // Step 2: Clear all previous chat states first
    clearAllChatStates();
    clearLocalStates();

    // Step 3: Set the new contact ID immediately for UI consistency
    dispatch(setselectedContactID(newContactId));

    // Step 4: Set expected contact to prevent race conditions
    dispatch(setExpectedContact(newContactId));

    // Step 5: Set active chat for socket
    setActiveChat(newContactId);

    // Step 6: Fetch all contact-related data in parallel for faster loading
    try {
      await Promise.all([
        dispatch(fetchMessagesByContact({ contactId: newContactId, limit: 50, offset: 0 })).unwrap(),
        dispatch(fetchContactById(newContactId)).unwrap(),
        dispatch(clearUnreadCount(newContactId))
      ]);

      // Use ref (not stale-closure state) to check if this contact is still current
      if (loadingContactIdRef.current === newContactId) {
        loadingContactIdRef.current = null;
        setLoadingContactId(null);
      }
    } catch (error) {
      console.error(`❌ Error loading contact ${newContactId}:`, error);

      if (loadingContactIdRef.current === newContactId) {
        loadingContactIdRef.current = null;
        setLoadingContactId(null);
      }
    } finally {
      if (selectUnifiedInFlightRef.current === newContactId) {
        selectUnifiedInFlightRef.current = null;
      }
    }
  }, [dispatch, clearLocalStates, clearAllChatStates]);

  // Extract chats from messages object with proper null checks
  // Use a stable reference for empty array to prevent unnecessary re-renders
  const emptyArrayRef = useRef([]);
  const chats = useMemo(
    () => (isTeamMemberChat
      ? emptyArrayRef.current // Team member chats are handled separately
      : (messages?.chats && messages.chats.length > 0 ? messages.chats : emptyArrayRef.current)),
    [isTeamMemberChat, messages]
  );
  
  // CRITICAL: Log when internal mention messages are in chats (for debugging)
  // Only log once per render cycle to avoid spam
  const internalMentionChats = chats.filter(msg => msg.status === "internal" || msg.isInternalMention === true);
  if (internalMentionChats.length > 0 && process.env.NODE_ENV === 'development') {
    // Log only the first internal mention to avoid spam
    const firstInternal = internalMentionChats[0];
    if (firstInternal && (!firstInternal.sendername || firstInternal.sendername === "Automated")) {
      console.warn(`⚠️ [CHATBODY] Internal mention in chats without sendername:`, {
        messageId: firstInternal.id || firstInternal.tempId,
        sendername: firstInternal.sendername,
        status: firstInternal.status,
        isInternalMention: firstInternal.isInternalMention,
        totalInternalMentions: internalMentionChats.length,
        totalChats: chats.length
      });
    }
  }
  
  // Transform team member messages to chat format for rendering
  const teamMemberChatsForRendering = useMemo(() => {
    if (!isTeamMemberChat || !teamMemberMessages || teamMemberMessages.length === 0) {
      return [];
    }
    
    return teamMemberMessages.map(msg => ({
      id: msg.id || msg.tempId,
      _id: msg.id || msg.tempId,
      tempId: msg.tempId,
      type: "chat",
      messageType: msg.messageType || "text",
      message: msg.message,
      body: msg.message,
      time: msg.timestamp,
      timestamp: msg.timestamp,
      status: msg.status,
      sendername: msg.fromMe ? (msg.senderName || "You") : undefined,
      clientname: msg.fromMe ? undefined : (msg.senderName || "Team Member"),
      replyBody: msg.replyBody,
      replyType: msg.replyType,
      replyId: msg.replyTo,
      replySenderName: undefined, // Can be populated if needed
      isInternalMention: false, // Team member chats are not internal mentions
      senderAvatarColor: msg.senderAvatarColor,
      ...(msg.documentFileName ? { documentFileName: msg.documentFileName } : {}),
      ...(msg.url ? { url: msg.url } : {}),
      ...(msg.mediaThumbnailUrl ? { mediaThumbnailUrl: msg.mediaThumbnailUrl } : {}),
      ...(msg.mediaFileSizeBytes != null && Number.isFinite(Number(msg.mediaFileSizeBytes))
        ? { mediaFileSizeBytes: Number(msg.mediaFileSizeBytes) }
        : {}),
      ...(msg.audioDurationSeconds != null && Number.isFinite(Number(msg.audioDurationSeconds))
        ? { audioDurationSeconds: Number(msg.audioDurationSeconds) }
        : {}),
    }));
  }, [isTeamMemberChat, teamMemberMessages]);
  
  // Combine chats for rendering
  const chatsToRender = isTeamMemberChat ? teamMemberChatsForRendering : chats;
  
  // Memoize the grouped chats with content-based comparison to prevent infinite re-renders
  const prevChatsContentRef = useRef(null);
  const prevGroupedChatsRef = useRef(null);
  
  const groupedChats = useMemo(() => {
    // Create a stable key from chats content for comparison
    // Only compare essential fields that affect rendering to avoid unnecessary recalculations
    const chatsKey = chatsToRender.length > 0 
      ? chatsToRender.map(chat => ({
          id: chat._id || chat.tempId || chat.id,
          time: chat.time,
          message: chat.message,
          messageType: chat.messageType,
          status: chat.status,
          type: chat.type,
          // Must include reactions or WhatsApp/IG/FB emoji updates never invalidate this cache
          reactionKey:
            chat.reaction?.emoji != null && chat.reaction?.emoji !== ""
              ? String(chat.reaction.emoji)
              : chat.reaction
                ? "1"
                : "",
          // Must include caption/media metadata, or a media message that gets its caption
          // (or thumbnail/size/filename) attached after its first render — e.g. a pending
          // message reconciled with server data — never invalidates this cache, leaving the
          // captionless version on screen until something unrelated forces a recompute.
          caption: chat.caption || "",
          mediaThumbnailUrl: chat.mediaThumbnailUrl || "",
          mediaFileSizeBytes: chat.mediaFileSizeBytes ?? "",
          documentFileName: chat.documentFileName || "",
        })).map(c => `${c.id}_${c.time}_${c.messageType}_${c.status}_${c.reactionKey}_${c.caption}_${c.mediaThumbnailUrl}_${c.mediaFileSizeBytes}_${c.documentFileName}`).join('|')
      : 'empty';
    
    // Only recalculate if content actually changed
    if (prevChatsContentRef.current === chatsKey && prevGroupedChatsRef.current) {
      return prevGroupedChatsRef.current;
    }
    
    // Team member chats: date separators via groupConsecutiveMessages (no consecutive media grouping)
    if (isTeamMemberChat) {
      const messagesWithoutDates = chatsToRender.filter(msg => msg.type !== "date");
      const sortedMessages = messagesWithoutDates.slice().sort((a, b) => {
        const timeA = new Date(getMessageTimestamp(a) || 0).getTime();
        const timeB = new Date(getMessageTimestamp(b) || 0).getTime();
        return timeA - timeB;
      });
      const grouped = groupConsecutiveMessages(sortedMessages);
      prevChatsContentRef.current = chatsKey;
      prevGroupedChatsRef.current = grouped;
      return grouped;
    }
    // User conversations: date separators only (same as groupConsecutiveMessages)
      const grouped = groupConsecutiveMessages(chatsToRender);
      prevChatsContentRef.current = chatsKey;
      prevGroupedChatsRef.current = grouped;
      return grouped;
  }, [chatsToRender, isTeamMemberChat]); // Depend on chatsToRender array - content comparison prevents unnecessary recalculations

  // Get the contactId from selectedContactID only - don't fallback to messages
  // This ensures we only show messages when a contact is actually selected
  const contactId = selectedContactID || null;
  
  // Check if contact exists in inbox data — memoized selector avoids re-scanning arrays on every render
  const contactExists = useSelector(selectContactExistsInInbox);
  // Bug fix (2026-09-26, found via live testing -- opening a group sometimes threw "Something
  // went wrong" and the header briefly showed "0 members"): the "Handle contact changes" effect
  // below watches selectedContactID/contactId the same way MessageCard.jsx's own URL-restore
  // effect does, but had no isGroup awareness at all -- unlike that effect (which explicitly
  // checks data.All for isGroup before calling selectGroup vs selectContact, see its own doc
  // comment), this one unconditionally called selectContactUnified, which fetches via
  // fetchMessagesByContact/fetchContactById -- both 404 ("Contact not found") for a group id,
  // since a group is never a ContactsModel row. selectGroup (invoked by MessageCard.jsx, either
  // from a row click or its own isGroup-aware restore effect) already fetches this group's data
  // correctly elsewhere -- this effect just needs to stay out of the way for a group id, not
  // duplicate that work. Same data.All lookup MessageCard.jsx already uses, so this can never
  // drift out of sync with what "is this id a group" actually means.
  const inboxRecentData = useSelector(selectInboxRecentData);
  const isGroupContact = useMemo(() => {
    if (!contactId) return false;
    const idStr = String(contactId);
    return !!inboxRecentData?.All?.some((item) => String(item.contactId) === idStr && item.isGroup);
  }, [contactId, inboxRecentData]);
  // True when route already points at contactId.
  // Also true when the route contains NO contact at all (URL not yet updated by Inbox.jsx's navigate effect).
  // Only false when the route contains a DIFFERENT contact id — meaning the user navigated away.
  const isRouteOnCurrentContact = useMemo(() => {
    if (!contactId) return false;
    const path = location.pathname || "";
    const idStr = String(contactId);
    // Route already shows this contact
    if (path.endsWith(`/chat/${idStr}`) || path.includes(`/chat/${idStr}/`)) return true;
    // Route has no /chat/ segment — URL hasn't updated yet; this is safe to proceed
    if (!path.includes("/chat/")) return true;
    // Route has a DIFFERENT /chat/:otherId — navigated away
    return false;
  }, [location.pathname, contactId]);
  
  // Debug: Check contactId value (removed to prevent infinite re-renders)

  // Track the previous contactId to detect changes.
  // Deliberately seeded null (not `contactId`) so that a fresh mount which
  // already has a contactId (e.g. remount after a breakpoint change wipes
  // this component and creates a new instance) still falls into the
  // "initial load" branch below and fetches — seeding it with `contactId`
  // made a brand-new instance look like it already knew about the contact,
  // permanently skipping the fetch and leaving the header stuck on its
  // loading skeleton.
  const prevContactIdRef = useRef(null);

  // Clear messages when component mounts - but only if no contact is selected.
  // NOTE: no cleanup here — clearAllChatStates() must NOT run on every contactId
  // change, only on true unmount. Running it here caused messages to be wiped
  // immediately after each fetch completed (when setselectedContactID fired and
  // changed contactId, React ran this cleanup before the next render).
  useEffect(() => {
    if (!contactId && !selectionInProgress()) {
      dispatch(clearMessages());
      clearLocalStates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- selectionInProgress only reads the live store
  }, [dispatch, clearLocalStates, contactId]);

  // Unmount-only cleanup — runs once when the ChatBody leaves the DOM.
  // Uses refs so the empty-deps closure always calls the latest callbacks.
  useEffect(() => {
    return () => {
      // React StrictMode (dev) runs this cleanup right after mount, i.e. in the middle of a chat open that
      // MessageCard's URL sync just started (selected + expected contact set, fetch pending). Wiping
      // messages there resets expectedContactId, the in-flight response is then dropped by the
      // late-response guard, and the chat never opens. Skip the store wipe only while a fetch for the
      // chat the URL still points at is in flight; a real unmount (leaving the inbox / another route)
      // changes the URL first and still clears everything.
      const live = store.getState();
      const expected = live.inboxMessage?.expectedContactId;
      const openingFromUrl =
        expected != null &&
        String(live.inboxAll?.selectedContactID || "") === String(expected) &&
        typeof window !== "undefined" &&
        window.location.pathname.includes(`/chat/${expected}`);
      if (!openingFromUrl) clearAllChatStatesRef.current();
      clearLocalStatesRef.current();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle contact changes by using unified contact selection
  useEffect(() => {
    // Guard: a group id must never go through the contact-specific fetch path below --
    // selectGroup (MessageCard.jsx) already owns fetching this group's data correctly.
    if (contactId && isGroupContact) {
      prevContactIdRef.current = contactId;
      return;
    }

    // Guard: if contact is no longer present in current inbox data (e.g. just soft-deleted)
    // or route has already moved away, do not re-select/fetch it.
    if (contactId && (!contactExists || !isRouteOnCurrentContact)) {
      prevContactIdRef.current = contactId;
      return;
    }

    // Skip only when this exact contact is already being fetched or its messages
    // are already loaded. A generic loading=true check is NOT sufficient here —
    // it would block switching to a different contact while A is still in-flight.
    const curMessages = messagesForGuardRef.current;
    const messagesAlreadyLoaded =
      curMessages &&
      String(curMessages.contactID || curMessages.contactId || '') === String(contactId);
    // In-flight check 1: selectContactUnified already running for this contact
    const fetchInFlightForThisContact = selectUnifiedInFlightRef.current === contactId;
    // In-flight check 2: useContactSelection hook already set expectedContactId for this contact
    // (it dispatches setExpectedContact before the fetch, so this is non-null while the hook is in-flight).
    // Deliberately NOT also requiring loadingForGuardRef here: setExpectedContact() and the
    // fetchMessagesByContact dispatch that follows it are both synchronous in the same call (no
    // await between them, see useContactSelection.js), so expectedContactId matching contactId is
    // already a reliable in-flight signal on its own. Requiring loading=true too left a real gap:
    // if this effect re-ran in the brief window after setExpectedContact() but before the fetch's
    // `pending` action flipped loading to true (e.g. a fast/cached response landing in that gap),
    // the guard failed, selectContactUnified fired a REDUNDANT clearAllChatStates()+fetch, and its
    // clearMessages() reset expectedContactId to null right as the hook's own in-flight response
    // arrived — tripping the "late response after clearMessages()" guard in InboxMessageSlice and
    // permanently dropping that data (visible as: Network shows a successful response, but the
    // pane stays on "No conversation selected"). Most reproducible on an already-cached contact
    // (e.g. reopening a just-closed chat), since the cached response resolves fast enough to land
    // in that gap.
    const hookInFlightForThisContact = String(expectedContactId || '') === String(contactId);
    const alreadyHandled = fetchInFlightForThisContact || hookInFlightForThisContact || messagesAlreadyLoaded;
    if (contactId && alreadyHandled) {
      prevContactIdRef.current = contactId;
      return;
    }

    // Fetch whenever we reach here with a contactId: the guards above already
    // ruled out "already loaded" / "already in flight" / "not selectable yet".
    // NOTE: previously this branched on prevContactIdRef (switch vs first-load)
    // but both branches called the identical selectContactUnified(contactId) —
    // and using that ref as a gate was actively harmful: the early-return guards
    // above set prevContactIdRef.current = contactId WITHOUT fetching (e.g. while
    // contactExists was momentarily false right after a remount), so once the
    // guard cleared on a later run, contactId === prevContactIdRef.current already
    // and the fetch never fired — a permanently stuck skeleton.
    if (contactId) {
      selectContactUnified(contactId);
    }

    // Update the previous contactId reference
    prevContactIdRef.current = contactId;
  }, [contactId, isGroupContact, contactExists, isRouteOnCurrentContact, selectContactUnified, expectedContactId]);

  // Clear local states whenever messages becomes null (from any clearMessages call)
  useEffect(() => {
    if (!messages) {
      clearLocalStates();
    }
  }, [messages, clearLocalStates]);

  // Fetch team member chat messages when team member chat is active
  useEffect(() => {
    if (isTeamMemberChat && teamMemberChat?.otherMemberId) {
      const otherMemberId = teamMemberChat.otherMemberId;
      
      // CRITICAL: Only fetch if we haven't fetched for this otherMemberId yet
      // This prevents infinite loops when the API returns an empty array
      if (hasFetchedTeamMemberRef.current !== otherMemberId) {
        dispatch(fetchTeamMemberChatMessages({ otherMemberId, limit: 50, offset: 0 }));
        // Mark that we've fetched for this otherMemberId
        hasFetchedTeamMemberRef.current = otherMemberId;
        // Reset offset when switching to a new team member chat
        prevContactIdRef.current = otherMemberId;
      }
      
      // CRITICAL: Only mark as read if there are messages (don't update timestamp for empty chats)
      // This prevents empty chats from moving to the top when opened
      if (teamMemberChat.otherMemberId && teamMemberMessages.length > 0) {
        dispatch(markTeamMemberChatAsRead(teamMemberChat.otherMemberId));
      }
      
      // Enable scroll loading after initial load completes
      if (!teamMemberLoading && teamMemberMessages.length > 0 && !initialLoadCompleteRef.current) {
        setScrollLoadingEnabled(true);
        initialLoadCompleteRef.current = true;
      }
    } else {
      // Reset when switching away from team member chat
      initialLoadCompleteRef.current = false;
      prevContactIdRef.current = null;
      hasFetchedTeamMemberRef.current = null;
    }
  }, [isTeamMemberChat, teamMemberChat?.otherMemberId, dispatch, teamMemberLoading, teamMemberMessages.length]);

  // Team member chat: if thread is open and a new incoming message arrives,
  // mark as read immediately so Recent unread badge does not increase.
  useEffect(() => {
    if (!isTeamMemberChat || !teamMemberChat?.otherMemberId || teamMemberLoading) {
      return;
    }
    if (!teamMemberMessages || teamMemberMessages.length === 0) {
      return;
    }

    const lastMessage = teamMemberMessages[teamMemberMessages.length - 1];
    const isIncoming = lastMessage && !lastMessage.fromMe;
    if (!isIncoming) {
      return;
    }

    dispatch(markTeamMemberChatAsRead(teamMemberChat.otherMemberId));
  }, [
    isTeamMemberChat,
    teamMemberChat?.otherMemberId,
    teamMemberMessages,
    teamMemberLoading,
    dispatch,
  ]);

  // Use a ref to track chats to avoid recreating callback on every chats change
  const chatsRef = useRef(chats);
  useEffect(() => {
    chatsRef.current = chats;
  }, [chats]);

  // Cache for scrollable parent — recomputed only when the DOM node changes
  const scrollableParentCache = useRef({ node: null, result: null });

  // Helper function to find the scrollable parent element (ChatBox column with overflowY: auto)
  const findScrollableParent = useCallback((element) => {
    if (!element) return null;

    if (scrollableParentCache.current.node === element) {
      return scrollableParentCache.current.result;
    }

    const walk = (el) => {
      if (!el) return null;
      const style = window.getComputedStyle(el);
      if (style.overflowY === "auto" || style.overflowY === "scroll") return el;
      return walk(el.parentElement);
    };

    const result = walk(element);
    scrollableParentCache.current = { node: element, result };
    return result;
  }, []);

  // Same threshold/formula as ChatBox.jsx's own SCROLL_BOTTOM_THRESHOLD_PX-based
  // check (that file's updateScrollToBottomVisibility) — used here to decide
  // whether the auto-scroll-on-new-message effect below is allowed to actually
  // move the viewport. Not shared/imported (small, self-contained duplicate)
  // since these are two different components' own scroll math, not a single
  // shared subsystem. Deliberately NOT applied to the separate initial-load
  // useLayoutEffect further down — that one must always jump to bottom
  // regardless of scroll position (there's nothing to "preserve" on first
  // open), only to this one, which also fires for messages arriving mid-read.
  const NEAR_BOTTOM_THRESHOLD_PX = 120;
  const isNearBottom = useCallback((element) => {
    const container = findScrollableParent(element);
    if (!container) return true; // no scrollable ancestor found — nothing to preserve, default to scrolling
    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    return distanceFromBottom <= NEAR_BOTTOM_THRESHOLD_PX;
  }, [findScrollableParent]);

  // Helper function to scroll to message within container only (prevents page scroll)
  const scrollToMessageInContainer = useCallback((messageElement, containerElement) => {
    if (!messageElement || !containerElement) return;
    
    // Get current scroll position and container dimensions
    const containerScrollTop = containerElement.scrollTop;
    const containerHeight = containerElement.clientHeight;
    
    // Calculate message position relative to container using getBoundingClientRect
    // This is more reliable than offsetTop when dealing with nested elements
    const containerRect = containerElement.getBoundingClientRect();
    const messageRect = messageElement.getBoundingClientRect();
    
    // Calculate the message's offset from the top of the scrollable content
    // Current scroll position + distance from container top to message top
    const messageOffsetTop = containerScrollTop + (messageRect.top - containerRect.top);
    const messageHeight = messageRect.height;
    
    // Calculate target scroll position to center the message in the container
    const targetScrollTop = messageOffsetTop - (containerHeight / 2) + (messageHeight / 2);
    
    // Ensure we don't scroll beyond the container bounds
    const maxScroll = containerElement.scrollHeight - containerHeight;
    const finalScrollTop = Math.max(0, Math.min(targetScrollTop, maxScroll));
    
    // Smooth scroll within the container only (prevents page scroll)
    containerElement.scrollTo({
      top: finalScrollTop,
      behavior: "smooth"
    });
  }, []);

  // Handler for scrolling to a message (with pagination support)
  const handleScrollToMessage = useCallback(async (id) => {
    if (!id || !contactId) return;
  
    const containerElement = chatContainerRef.current;
    if (!containerElement) return;

    // Block generic "scroll to bottom" while we jump — it runs on chats/loading changes and would undo this scroll.
    suppressAutoScrollToBottomRef.current = true;
    const scheduleReleaseSuppressAfterReplyJump = () => {
      window.setTimeout(() => {
        suppressAutoScrollToBottomRef.current = false;
      }, 650);
    };

    const normalizedId = String(id).trim();
    const refKeysForId = [normalizedId];
    if (id !== normalizedId) refKeysForId.push(String(id));
  
    // Function to highlight a message briefly
    const highlightMessage = (messageElement) => {
      if (!messageElement) return;
      messageElement.style.transition = "background-color 0.5s ease";
      const originalColor = messageElement.style.backgroundColor;
      messageElement.style.backgroundColor = "rgba(255, 255, 0, 0.3)";
      setTimeout(() => {
        messageElement.style.backgroundColor = originalColor;
      }, 2000);
    };
  
    // Scroll only inside the chat column (never scrollIntoView — avoids moving the whole Inbox under the top nav).
    const scrollAndHighlight = (messageElement) => {
      if (!messageElement || !containerElement) return;
      const scrollParent = findScrollableParent(containerElement) || containerElement;
      scrollToMessageInContainer(messageElement, scrollParent);
      highlightMessage(messageElement);
    };

    const resolveMessageElement = () => {
      for (const key of refKeysForId) {
        const el = messageRefs.current[key];
        if (el) return el;
      }
      return null;
    };
  
    // Check if the message is already loaded (match id / _id / tempId like message refs)
    const messageExists = chatsRef.current.some((chat) => {
      const cid = chat.id ?? chat._id ?? chat.tempId;
      if (cid == null) return false;
      return String(cid).trim() === normalizedId;
    });
  
    console.log("[REPLY-JUMP] handleScrollToMessage", {
      contactId,
      messageIdPrefix: normalizedId.slice(0, 48),
      messageExists,
      chatsCount: chatsRef.current?.length ?? 0,
    });

    if (messageExists) {
      const messageElement = resolveMessageElement();
      if (!messageElement) {
        console.warn("[REPLY-JUMP] message in list but ref missing; retrying next frame", {
          messageIdPrefix: normalizedId.slice(0, 48),
        });
        requestAnimationFrame(() => {
          const el = resolveMessageElement();
          if (el) {
            scrollAndHighlight(el);
            scheduleReleaseSuppressAfterReplyJump();
          } else {
            scheduleReleaseSuppressAfterReplyJump();
          }
        });
        return;
      }
      scrollAndHighlight(messageElement);
      scheduleReleaseSuppressAfterReplyJump();
    } else {
      // Message is not loaded, fetch messages around it
      setIsLoadingTargetMessage(true);
      setTargetMessageId(normalizedId);
  
      try {
        await dispatch(fetchMessagesAroundMessage({ contactId, messageId: normalizedId, limit: 50 })).unwrap();
  
        const tryScrollAfterFetch = (attempt) => {
          const messageElement = resolveMessageElement();
          if (messageElement) {
            console.log("[REPLY-JUMP] scroll after fetch OK", {
              attempt,
              messageIdPrefix: normalizedId.slice(0, 48),
            });
            scrollAndHighlight(messageElement);
            setIsLoadingTargetMessage(false);
            setTargetMessageId(null);
            scheduleReleaseSuppressAfterReplyJump();
            return;
          }
          if (attempt < 12) {
            requestAnimationFrame(() => tryScrollAfterFetch(attempt + 1));
          } else {
            console.warn("[REPLY-JUMP] ref still missing after fetch (max attempts)", {
              messageIdPrefix: normalizedId.slice(0, 48),
            });
            setIsLoadingTargetMessage(false);
            setTargetMessageId(null);
            scheduleReleaseSuppressAfterReplyJump();
          }
        };

        requestAnimationFrame(() => tryScrollAfterFetch(0));
      } catch (error) {
        console.error("[REPLY-JUMP] Error fetching messages around target:", error);
        setIsLoadingTargetMessage(false);
        setTargetMessageId(null);
        scheduleReleaseSuppressAfterReplyJump();
      }
    }
  }, [contactId, dispatch, findScrollableParent, scrollToMessageInContainer]);
  
  // Added scrollToMessageInContainer to dependencies

  const formatTime = (timestamp) => {
    return formatChatTime(timestamp);
  };

 

  // Workflow/automated messages (WhatsApp & Instagram) show on the RIGHT with label "Automated"
  const getDisplayNames = (msg) => {
    if (!msg) return { sendername: undefined, clientname: undefined };

    const sid = msg.senderid ?? msg.senderId;
    const hasTeamSender = sid != null && sid !== "";
    const currentUser = user ?? null;
    const uid = currentUser?._id ?? currentUser?.id;
    const isFromCurrentTeamMember =
      hasTeamSender && uid && String(sid) === String(uid);
    // Never show "Automated" for our own outbound sends when label was corrupted in Redux/socket
    if (isFromCurrentTeamMember && currentUser?.name) {
      const sn = msg.sendername;
      const bad =
        !sn ||
        sn === "Automated" ||
        (typeof sn === "string" && sn.toLowerCase() === "automated");
      if (bad) {
        return { sendername: currentUser.name, clientname: msg.clientname };
      }
    }

    // Use central helper for all sender display resolution (performedBy-first, legacy fallback)
    const isAutomatedName =
      msg.sendername === "Automated" ||
      (msg.sendername && String(msg.sendername).toLowerCase() === "automated");
    const hasRealNonAutomatedSendername =
      msg.sendername &&
      msg.sendername !== "Automated" &&
      String(msg.sendername).toLowerCase() !== "automated";
    const isWorkflowByIds =
      !msg.senderid &&
      !msg.senderId &&
      !msg.clientid &&
      !hasRealNonAutomatedSendername &&
      (isAutomatedName || (!msg.clientname && !msg.clientid));
    const idStr = msg.id ? String(msg.id) : "";
    const tempIdStr = msg.tempId ? String(msg.tempId) : "";
    const hasWorkflowPrefix = idStr.startsWith("workflow_") || tempIdStr.startsWith("workflow_");
    const isWorkflowFlag = msg.isWorkflowMsg === true || msg.isBulkMessage === true;
    const isInstagram = msg.platform === "instagram";
    const legacyIsWorkflowAutomated =
      isWorkflowByIds ||
      (isAutomatedName && !hasTeamSender) ||
      hasWorkflowPrefix ||
      isWorkflowFlag ||
      (isInstagram && !hasTeamSender && (isAutomatedName || hasWorkflowPrefix));

    const resolved = resolveDisplaySender({
      performedBy: msg.performedBy,
      performedByName: msg.performedByName,
      senderName: msg.sendername,
      isWorkflowAutomated: msg.isWorkflowAutomated === true || legacyIsWorkflowAutomated,
      isBulkMessage: msg.isBulkMessage === true,
    });

    if (resolved.isWorkflowAutomated) {
      return { sendername: "Automated", clientname: undefined };
    }
    return { sendername: resolved.sendername, clientname: msg.clientname };
  };

  // Pill-bubble grouping: a message is a grouping candidate if it's one of the pill-rendered
  // kinds. `msg.type === "chat"` covers plain text, catalog/list, AND location/contacts
  // (distinguished only by messageType) — pillShapeKey keeps those from merging into each
  // other's group even though they share the same outer type, so a group never straddles two
  // different bubble shapes. The remaining social/reaction types (story_reply, post_comment,
  // ctwa_referral, live_comment, instagram_reaction, facebook_reaction, interactive) each carry
  // their own `msg.type`, so pillShapeKey's fallback (`return m.type`) already keys them apart
  // without needing their own branch here.
  const PILL_GROUP_OWN_TYPES = [
    "text", "chat", "template", "catalog",
    "story_reply", "post_comment", "ctwa_referral", "live_comment",
    "instagram_reaction", "facebook_reaction", "interactive",
  ];
  const isPillGroupCandidate = (m) => !!m && PILL_GROUP_OWN_TYPES.includes(m.type);
  const pillShapeKey = (m) => {
    if (m.type === "chat") {
      if (m.messageType && m.messageType !== "text") return m.messageType;
      return "text";
    }
    return m.type;
  };
  const isSamePillGroup = (a, b) => {
    if (!isPillGroupCandidate(a) || !isPillGroupCandidate(b)) return false;
    if (pillShapeKey(a) !== pillShapeKey(b)) return false;
    const namesA = getDisplayNames(a);
    const namesB = getDisplayNames(b);
    const aFromMe = !!namesA.sendername && !namesA.clientname;
    const bFromMe = !!namesB.sendername && !namesB.clientname;
    if (aFromMe !== bFromMe) return false;
    // A WhatsApp Webchat message (black badge) and a Cloud API one (green badge) never share a group: the group shows a single
    // avatar badge, so merging them would hide one of the two icons.
    if (getMessageBadgePlatform(a, messages?.platform) !== getMessageBadgePlatform(b, messages?.platform)) return false;
    // Same direction isn't enough — "Automated" and a real agent are both
    // fromMe:true but must never merge into one group (Rahim, 2026-08-25:
    // "there is no automated message" — its name was getting swallowed by
    // a neighbouring human agent's group).
    const nameA = namesA.sendername || namesA.clientname || "";
    const nameB = namesB.sendername || namesB.clientname || "";
    return nameA === nameB;
  };

  // Scroll to bottom when a new message arrives or component initially loads
  useEffect(() => {
    // For team member chats
    if (isTeamMemberChat) {
      const shouldScroll =
        teamMemberChat?.otherMemberId &&
        chatContainerRef.current &&
        !isFetchingOlderRef.current &&
        teamMemberMessages.length > 0 &&
        !teamMemberLoading &&
        !suppressAutoScrollToBottomRef.current &&
        // Always scroll on first opening this chat (nothing to preserve yet);
        // once loaded, only follow new messages down if the user is already
        // near the bottom — don't yank them back mid-read (Rahim, 2026-08-29:
        // "i think it auto scrolls as well can you check"). Team-member chats
        // have no separate initial-load layout effect (that one explicitly
        // skips isTeamMemberChat), so this branch is the only thing that
        // scrolls them to bottom at all — the initialLoadCompleteRef bypass
        // here is load-bearing, not just defensive.
        (!initialLoadCompleteRef.current || isNearBottom(chatContainerRef.current));

      if (shouldScroll) {
        // Find the actual scrollable container (parent element with overflowY: auto)
        const scrollableContainer = findScrollableParent(chatContainerRef.current);
        const containerToScroll = scrollableContainer || chatContainerRef.current;

        // Double-rAF: first frame lets React flush layout, second frame runs after paint
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (containerToScroll) {
              containerToScroll.scrollTop = containerToScroll.scrollHeight;

              if (!initialLoadCompleteRef.current) {
                initialLoadCompleteRef.current = true;
                setScrollLoadingEnabled(true);
              }
            }
          });
        });
      }
      return; // Exit early for team member chats
    }

    // For user chats (existing logic)
    // Only scroll if:
    // 1. We have a contact selected
    // 2. Messages are loaded and match the selected contact
    // 3. Chats are available
    // 4. Not fetching older messages
    // 5. Loading is complete
    // Bug fix (2026-09-01, Rahim: "scroll up, type+send a message, it should then auto
    // scroll me back down"): the newest chat entry is our own message the instant it's
    // sent — TextBox adds it optimistically with status:"pending" before the server
    // confirms it. That's a deliberate, unambiguous "the user just sent this" signal
    // (unlike an inbound message, which never carries this status), so bypass the
    // isNearBottom gate for it — same as a real chat app always following your own send
    // to the bottom, while still not yanking you down for messages arriving while you're
    // reading scrollback.
    const lastChat = chats.length > 0 ? chats[chats.length - 1] : null;
    const justSentByMe = lastChat?.status === "pending";
    const shouldScroll =
      contactId &&
      chatContainerRef.current &&
      !isFetchingOlderRef.current &&
      chats.length > 0 &&
      !loading &&
      !isLoadingTargetMessage &&
      !suppressAutoScrollToBottomRef.current &&
      messages &&
      messages.contactID === contactId &&
      // Same reasoning as the team-member branch above — the separate
      // initial-load useLayoutEffect further down already handles true first
      // opens synchronously (and by then has already set this ref + scrolled,
      // so isNearBottom reads true naturally); this bypass just covers it
      // defensively too instead of relying solely on effect-ordering timing.
      (!initialLoadCompleteRef.current || isNearBottom(chatContainerRef.current) || justSentByMe);

    if (shouldScroll) {
      // Find the actual scrollable container (parent element with overflowY: auto)
      const scrollableContainer = findScrollableParent(chatContainerRef.current);
      const containerToScroll = scrollableContainer || chatContainerRef.current;

      // Double-rAF: first frame lets React flush layout, second frame runs after paint
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (containerToScroll) {
            containerToScroll.scrollTop = containerToScroll.scrollHeight;

            if (!initialLoadCompleteRef.current) {
              initialLoadCompleteRef.current = true;
              setScrollLoadingEnabled(true);
            }
          }
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-scrolls on chats.length, not on every chats identity change
  }, [
    isTeamMemberChat,
    teamMemberChat?.otherMemberId,
    teamMemberMessages.length,
    teamMemberLoading,
    chats.length,
    contactId,
    loading,
    isLoadingTargetMessage,
    messages,
    findScrollableParent,
    isNearBottom
  ]);

  // Synchronous scroll-to-bottom on initial load of a new contact.
  // useLayoutEffect fires before the browser paints, eliminating the visible
  // top→bottom jump that happened with the async useEffect approach.
  // Only fires on the very first render for each contact (initialLoadCompleteRef guard).
  useLayoutEffect(() => {
    if (
      !contactId ||
      loading ||
      !messages ||
      messages.contactID !== contactId ||
      chats.length === 0 ||
      isFetchingOlderRef.current ||
      suppressAutoScrollToBottomRef.current ||
      initialLoadCompleteRef.current ||
      isTeamMemberChat
    ) {
      return;
    }

    const container = findScrollableParent(chatContainerRef.current) || chatContainerRef.current;
    if (!container) return;

    container.scrollTop = container.scrollHeight;
    initialLoadCompleteRef.current = true;
    setScrollLoadingEnabled(true);
  }, [contactId, loading, messages, chats.length, isTeamMemberChat, findScrollableParent]);

  // Maintain scroll position after loading older messages
  useEffect(() => {
    if (!loading && isFetchingOlderRef.current && chatContainerRef.current) {
      const scrollableContainer = findScrollableParent(chatContainerRef.current);
      const containerToScroll = scrollableContainer || chatContainerRef.current;
      
      const newScrollHeight = containerToScroll.scrollHeight;
      const scrollDifference = newScrollHeight - prevScrollHeightRef.current;

      requestAnimationFrame(() => {
        if (containerToScroll) {
          containerToScroll.scrollTop = scrollDifference;
        }
        isFetchingOlderRef.current = false;
      });
    }
  }, [loading, chats.length, findScrollableParent]); // Added findScrollableParent to dependencies

  // Improved scroll handler with pagination support for both user conversations and team member chats
  const handleScroll = useCallback(() => {
    // Handle team member chat pagination
    if (isTeamMemberChat && teamMemberChat?.otherMemberId) {
      if (!scrollLoadingEnabled || !teamMemberChat) return;

      if (chatContainerRef.current) {
        const scrollableContainer = findScrollableParent(chatContainerRef.current);
        const containerToScroll = scrollableContainer || chatContainerRef.current;
        
        // Check if user scrolled near the top (within 100px)
        const scrollTop = containerToScroll.scrollTop;
        if (scrollTop <= 100) {
          // Check if we have more messages to load
          const hasMoreMessages = teamMemberChat.hasMore !== false && 
                                  teamMemberChat.totalMessages > teamMemberMessages.length;
          
          if (
            !teamMemberLoading &&
            !isFetchingOlderRef.current &&
            !isLoadingOlder &&
            hasMoreMessages
          ) {
            console.log("📜 Scrolling to load older team member messages", {
              hasMore: teamMemberChat.hasMore,
              totalMessages: teamMemberChat.totalMessages,
              loadedMessages: teamMemberMessages.length,
              offset: teamMemberChat.offset
            });
            
            prevScrollHeightRef.current = containerToScroll.scrollHeight;
            isFetchingOlderRef.current = true;
            setIsLoadingOlder(true);

            // Fetch older messages using pagination for team member chats
            dispatch(fetchOlderTeamMemberMessages({ otherMemberId: teamMemberChat.otherMemberId, limit: 50 }))
              .unwrap()
              .then(() => {
                console.log("✅ Older team member messages loaded successfully");
                setIsLoadingOlder(false);
                isFetchingOlderRef.current = false;
              })
              .catch((error) => {
                console.error("❌ Error loading older team member messages:", error);
                setIsLoadingOlder(false);
                isFetchingOlderRef.current = false;
              });
          }
        }
      }
      return; // Don't proceed with user conversation handling
    }

    // Handle user conversation pagination (existing logic)
    if (!scrollLoadingEnabled || !messages || !contactId) return;

    if (chatContainerRef.current) {
      const scrollableContainer = findScrollableParent(chatContainerRef.current);
      const containerToScroll = scrollableContainer || chatContainerRef.current;
      
      // Check if user scrolled near the top (within 100px)
      const scrollTop = containerToScroll.scrollTop;
      if (scrollTop <= 100) {
        // Check if we have more messages to load
        const hasMoreMessages = messages.hasMore !== false && 
                                messages.totalChats > (messages.loadedChats || chats.length);
        
        if (
          !loading &&
          !isFetchingOlderRef.current &&
          !isLoadingOlder &&
          hasMoreMessages
        ) {
          console.log("📜 Scrolling to load older messages", {
            hasMore: messages.hasMore,
            totalChats: messages.totalChats,
            loadedChats: messages.loadedChats || chats.length,
            offset: messages.offset
          });
          
          prevScrollHeightRef.current = containerToScroll.scrollHeight;
          isFetchingOlderRef.current = true;
          setIsLoadingOlder(true);

          // Fetch older messages using pagination
          dispatch(fetchOlderMessages({ contactId, limit: 50 }))
            .unwrap()
            .then(() => {
              console.log("✅ Older messages loaded successfully");
              setIsLoadingOlder(false);
              isFetchingOlderRef.current = false;
            })
            .catch((error) => {
              console.error("❌ Error loading older messages:", error);
              setIsLoadingOlder(false);
              isFetchingOlderRef.current = false;
            });
        }
      }
    }
  }, [
    chatContainerRef,
    chats.length,
    loading,
    messages,
    contactId,
    scrollLoadingEnabled,
    isLoadingOlder,
    dispatch,
    findScrollableParent,
    isTeamMemberChat,
    teamMemberChat,
    teamMemberMessages,
    teamMemberLoading,
  ]);

  // Set up scroll event listener — batched to once per animation frame and
  // marked passive, same pattern as ChatBox.jsx's own scroll listener
  // (scrollRafRef there). handleScroll's own fetch-trigger logic and its
  // duplicate-fetch guards (isFetchingOlderRef/isLoadingOlder) are untouched;
  // this only changes how often it's invoked during a scroll, not what it does.
  useEffect(() => {
    const container = chatContainerRef.current;
    if (container) {
      const scrollableContainer = findScrollableParent(container);
      const containerToListen = scrollableContainer || container;

      if (scrollLoadingEnabled && containerToListen) {
        let rafPending = false;
        const onScroll = () => {
          if (rafPending) return;
          rafPending = true;
          requestAnimationFrame(() => {
            rafPending = false;
            handleScroll();
          });
        };
        containerToListen.addEventListener("scroll", onScroll, { passive: true });
        return () => containerToListen.removeEventListener("scroll", onScroll);
      }
    }
  }, [handleScroll, scrollLoadingEnabled, findScrollableParent]);

  // Track if we've already marked messages as read for this contact to prevent duplicate calls
  const markedAsReadRef = useRef(null);

  useEffect(() => {
    const onVis = () => {
      if (typeof document !== "undefined" && document.visibilityState === "visible") {
        setDocVisibilityEpoch((n) => n + 1);
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);
  
  // Mark messages as read when a chat is opened (with debouncing)
  useEffect(() => {
    if (isTeamMemberChat || !contactId) {
      return;
    }

    // Reset ref when contactId changes (switching to a different chat)
    const idStr = String(contactId);
    if (markedAsReadRef.current && !markedAsReadRef.current.startsWith(idStr)) {
      markedAsReadRef.current = null;
    }

    const path = location.pathname || "";
    const onInboxChatRoute =
      path.includes("/inbox/") &&
      (path.endsWith(`/chat/${idStr}`) || path.includes(`/chat/${idStr}/`));
    const docVisible = typeof document === "undefined" || document.visibilityState === "visible";

    // Align with URL: do not call mark-as-read when another route is shown or /chat/:id is not this contact
    if (!onInboxChatRoute || !docVisible) {
      return;
    }

    const messagesMatchContact =
      messages &&
      String(messages.contactID || messages.contactId || "") === idStr;
    const eligibleForAutoRead =
      contactExists ||
      (messagesMatchContact && Array.isArray(messages.chats) && messages.chats.length > 0);

    if (!eligibleForAutoRead) {
      return;
    }
    
    // Only proceed if we have messages AND chats loaded
    // CRITICAL: For Instagram/Facebook contacts, contactNumber might be null, so check for contactId or platform
    const isInstagram = messages?.platform === "instagram";
    const isFacebook = messages?.platform === "facebook";
    const isWebsite = messages?.platform === "website";
    // Website contacts have no phoneNumberID (no WhatsApp identity) — same
    // contactId-based mark-as-read path as Instagram/Facebook, not the
    // phoneNumberID-based WhatsApp path below. Without this, hasContactIdentifier
    // below is never true for a website contact (no contactNumber, and
    // contactIdForMarkRead only gets set for the platforms in this list), so
    // markMessageAsRead is never even dispatched.
    const useContactIdForPlatform = isInstagram || isFacebook || isWebsite;
    const hasContactIdentifier = messages?.contactNumber || (useContactIdForPlatform && contactId);

    if (messages && hasContactIdentifier && messages.chats && messages.chats.length > 0) {
      // Get AccountID from Redux state (not localStorage for security)
      if (!user) {
        return;
      }

      const AccountID = user.accountId;

      // Get contact's phone number or contactId from the messages object
      // For Instagram/Facebook: use contactId; for WhatsApp: use phoneNumberID
      let phoneNumberID = null;
      let contactIdForMarkRead = null;
      
      if (useContactIdForPlatform) {
        contactIdForMarkRead = contactId || messages.contactID;
      } else {
        // For WhatsApp, use phoneNumberID
        if (messages.contactNumber) {
          phoneNumberID = messages.contactNumber;
        }
      }

      if (AccountID && (phoneNumberID || contactIdForMarkRead)) {
        // Set this as the active chat for socket handling
        setActiveChat(messages.contactID || contactId);

        // Check if there are any unread incoming messages
        const hasUnreadMessages = unreadIncomingMessages.length > 0;

        // Unique key for this contact — reset when contact changes (handled above)
        const contactKey = `${contactId}-${phoneNumberID || contactIdForMarkRead}`;

        // Only dispatch if there are unread messages AND we haven't already sent the
        // mark-as-read for this contact in the current session.  The ref prevents
        // the effect from firing again every time chats.length or another dep changes
        // while the contact is still open.
        const alreadyMarked = markedAsReadRef.current === contactKey;
        const shouldMarkAsRead = hasUnreadMessages && !alreadyMarked;

        if (shouldMarkAsRead) {
          markedAsReadRef.current = contactKey;
          
          // Mark as read when chat is open
        const timeoutId = setTimeout(() => {
          if (typeof document !== "undefined" && document.visibilityState !== "visible") {
            return;
          }
          const p = window.location?.pathname || "";
          if (
            !p.includes("/inbox/") ||
            !(p.endsWith(`/chat/${idStr}`) || p.includes(`/chat/${idStr}/`))
          ) {
            return;
          }
          const markReadTargetId = contactId || messages.contactID || contactIdForMarkRead;
          if (!claimMarkAsReadDispatch(markReadTargetId)) {
            return;
          }
          dispatch(
            markMessageAsRead({
              AccountID,
              to: phoneNumberID || contactIdForMarkRead, // Use contactId for Instagram, phoneNumberID for WhatsApp
              // Always send Mongo contact id so inbox Recent badge can clear on fulfilled (WhatsApp + IG/FB)
              contactId: contactId || messages.contactID || contactIdForMarkRead,
              platform: isInstagram ? "instagram" : isFacebook ? "facebook" : isWebsite ? "website" : "whatsapp", // Include platform info
              autoMarkAsRead: true, // Auto-mark since chat is open
            })
          );
        }, 300); // 300ms debounce for faster response

        return () => clearTimeout(timeoutId);
      }
    }
    }
  }, [
    isTeamMemberChat,
    contactId,
    unreadIncomingMessages.length,
    dispatch,
    location.pathname,
    docVisibilityEpoch,
    contactExists,
    messages,
    messages?.contactId,
    messages?.contactNumber,
    messages?.platform,
    user,
  ]);

  // Set/clear active chat when component mounts/unmounts
  useEffect(() => {
    // When the component mounts and we have a contactId, set it as active
    if (contactId) {
      setActiveChat(contactId);
    } else {
      // Clear active chat when no contact is selected
      clearActiveChat();
    }

    // When the component unmounts, clear the active chat
    return () => {
      clearActiveChat();
      messageMarkedRef.current = {};
    };
  }, [contactId]);

  // Clear messages when no contact is selected
  useEffect(() => {
    if (!contactId && !selectionInProgress()) {
      dispatch(clearMessages());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- selectionInProgress only reads the live store
  }, [contactId, dispatch]);

  // Loading timeout to prevent getting stuck
  useEffect(() => {
    if (loading && contactId) {
      // Clear any existing timeout
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
      
      // Set a timeout to prevent loading from getting stuck
      loadingTimeoutRef.current = setTimeout(() => {
        // Guard retries: avoid re-fetching contacts that were removed from current inbox
        // or when route already moved away from this contact.
        if (!contactExists || !isRouteOnCurrentContact) {
          return;
        }
        // Guard: don't fire a duplicate request if selectContactUnified's own fetch for
        // this contact is still in flight — let that one resolve (or itself get retried).
        if (selectUnifiedInFlightRef.current === contactId) {
          return;
        }
        // Guard: a group id must never retry via the contact-specific fetch (same reasoning
        // as the "Handle contact changes" effect's own isGroupContact guard above) -- a slow
        // fetchMessagesByGroup would otherwise get "corrected" into a doomed
        // fetchMessagesByContact call that 404s for a group id.
        if (isGroupContact) {
          return;
        }
        console.warn(`⚠️ Loading timeout for contact ${contactId}, forcing retry`);
        setIsLoadingTimeout(true);
        
        // Try to fetch messages again
        dispatch(fetchMessagesByContact({ contactId, limit: 50, offset: 0 }));
        
        // Reset timeout state after a short delay
        setTimeout(() => {
          setIsLoadingTimeout(false);
        }, 1000);
      }, 10000); // 10 second timeout
      
      return () => {
        if (loadingTimeoutRef.current) {
          clearTimeout(loadingTimeoutRef.current);
          loadingTimeoutRef.current = null;
        }
      };
    } else {
      // Clear timeout when not loading
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
      setIsLoadingTimeout(false);
    }
  }, [loading, contactId, contactExists, isRouteOnCurrentContact, isGroupContact, dispatch]);

  // CRITICAL: Check team member chat FIRST before checking contactId
  // For team member chats, contactId will be null, so we need to handle them separately
  if (isTeamMemberChat) {
    // Show loader when loading, even if otherMemberId is set (matches user chat behavior)
    if (teamMemberLoading) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            flexDirection: "column",
            gap: 3,
            backgroundColor: (theme) => theme.palette.background.default,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
              padding: 4,
              borderRadius: 2,
              backgroundColor: (theme) => theme.palette.background.paper,
              boxShadow: (theme) => theme.shadows[1],
            }}
          >
            <CircularProgress
              size={48}
              thickness={4}
              sx={{
                color: (theme) => theme.palette.primary.main,
              }}
            />
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{
                fontWeight: 500,
                textAlign: "center",
              }}
            >
              Loading conversation...
            </Typography>
          </Box>
        </Box>
      );
    }
    
    // Check if no team member is selected (more explicit check)
    const hasNoTeamMemberSelected = !teamMemberChat || 
      (!teamMemberChat.otherMemberId && !teamMemberChat.otherMember);
    
    if (hasNoTeamMemberSelected && !teamMemberLoading) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            flexDirection: "column",
            px: 4,
          }}
        >
          <Box sx={{ mb: 1.5, color: (theme) => theme.palette.text.secondary, display: "flex" }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none">
              <g>
                <path d="M12 22.7498C11.3 22.7498 10.59 22.4798 10.06 21.9498L8.34998 20.2598C7.92998 19.8398 7.35001 19.6097 6.76001 19.6097H6C3.93 19.6097 2.25 17.9398 2.25 15.8898V4.97974C2.25 2.92974 3.93 1.25977 6 1.25977H18C20.07 1.25977 21.75 2.92974 21.75 4.97974V15.8898C21.75 17.9398 20.07 19.6097 18 19.6097H17.24C16.65 19.6097 16.07 19.8498 15.65 20.2598L13.94 21.9498C13.41 22.4798 12.7 22.7498 12 22.7498ZM6 2.74976C4.76 2.74976 3.75 3.74973 3.75 4.96973V15.8798C3.75 17.1098 4.76 18.0997 6 18.0997H6.76001C7.75001 18.0997 8.70997 18.4998 9.40997 19.1898L11.12 20.8798C11.61 21.3598 12.4 21.3598 12.89 20.8798L14.6 19.1898C15.3 18.4998 16.26 18.0997 17.25 18.0997H18C19.24 18.0997 20.25 17.0998 20.25 15.8798V4.96973C20.25 3.73973 19.24 2.74976 18 2.74976H6Z" fill="currentColor" />
                <path d="M11.9999 10.7498C10.2999 10.7498 8.91992 9.3698 8.91992 7.6698C8.91992 5.9698 10.2999 4.58984 11.9999 4.58984C13.6999 4.58984 15.08 5.9698 15.08 7.6698C15.08 9.3698 13.6999 10.7498 11.9999 10.7498ZM11.9999 6.08984C11.1299 6.08984 10.4199 6.7998 10.4199 7.6698C10.4199 8.5398 11.1299 9.24982 11.9999 9.24982C12.8699 9.24982 13.58 8.5398 13.58 7.6698C13.58 6.7998 12.8699 6.08984 11.9999 6.08984Z" fill="currentColor" />
                <path d="M16 16.4103C15.59 16.4103 15.25 16.0703 15.25 15.6603C15.25 14.2803 13.79 13.1504 12 13.1504C10.21 13.1504 8.75 14.2803 8.75 15.6603C8.75 16.0703 8.41 16.4103 8 16.4103C7.59 16.4103 7.25 16.0703 7.25 15.6603C7.25 13.4503 9.38 11.6504 12 11.6504C14.62 11.6504 16.75 13.4503 16.75 15.6603C16.75 16.0703 16.41 16.4103 16 16.4103Z" fill="currentColor" />
              </g>
            </svg>
          </Box>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: { xs: "0.9375rem", sm: "1rem", md: "1.05rem", lg: "1.05rem", xl: "1.05rem" },
              color: "text.primary",
              mb: 0.5,
            }}
          >
            No team member selected
          </Typography>
          <Typography sx={{ fontSize: "0.875rem", color: "text.secondary", textAlign: "center", mb: 2 }}>
            Select a team member to view messages
          </Typography>
        </Box>
      );
    }
    
    /*
    // Show error state if there's an error and not loading
    if (teamMemberError && !teamMemberLoading) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            flexDirection: "column",
            gap: 2,
            padding: 4,
          }}
        >
          <Typography color="error" variant="h6" textAlign="center">
            Failed to load conversation
          </Typography>
          <Typography color="text.secondary" variant="body2" textAlign="center">
            {typeof teamMemberError === "string"
              ? teamMemberError
              : typeof teamMemberError === "object" && teamMemberError?.message
              ? teamMemberError.message
              : "Something went wrong while loading messages"}
          </Typography>
          <Button
            variant="outlined"
            onClick={() => {
              if (teamMemberChat?.otherMemberId) {
                dispatch(fetchTeamMemberChatMessages({ otherMemberId: teamMemberChat.otherMemberId, limit: 50, offset: 0 }));
              }
            }}
            sx={{ mt: 1 }}
          >
            Try Again
          </Button>
        </Box>
      );
    }
    */
    
    // Show empty state when not loading, no error, and no messages
    if ((!teamMemberMessages || teamMemberMessages.length === 0) && !teamMemberLoading && !teamMemberError) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            flexDirection: "column",
            gap: 2,
            padding: 4,
          }}
        >
          <Typography color="text.secondary" variant="h6" textAlign="center">
            Start the conversation with {teamMemberChat?.otherMember?.name || "this team member"}
          </Typography>
        </Box>
      );
    }
  }

  // If no contact is selected (and NOT a team member chat), show a message indicating to select a contact.
  // Guard: skip this when loading=true — we're mid-fetch on the first click where
  // selectedContactID is still null (setselectedContactID fires only post-fetch).
  // In that case shouldShowLoading (using effectiveContactId) will render the skeleton.
  if (!contactId && !isTeamMemberChat && !loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          flexDirection: "column",
          px: 4,
        }}
      >
        {/* "No conversation selected" (isChannelConnected true) dropped the
            old image (Rahim, 2026-08-31: "only keep the heading and sub para
            delete the image") in favor of this small iconsax-style icon
            instead. "No channel connected" keeps the original illustration —
            a more consequential state, wasn't part of this ask. */}
        {isChannelConnected ? (
          <Box sx={{ mb: 1.5, color: (theme) => theme.palette.text.secondary, display: "flex" }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none">
              <g>
                <path d="M17.98 10.79V14.79C17.98 15.05 17.97 15.3 17.94 15.54C17.71 18.24 16.12 19.58 13.19 19.58H12.79C12.54 19.58 12.3 19.7 12.15 19.9L10.95 21.5C10.42 22.21 9.56 22.21 9.03 21.5L7.82999 19.9C7.69999 19.73 7.41 19.58 7.19 19.58H6.79001C3.60001 19.58 2 18.79 2 14.79V10.79C2 7.86001 3.35001 6.27001 6.04001 6.04001C6.28001 6.01001 6.53001 6 6.79001 6H13.19C16.38 6 17.98 7.60001 17.98 10.79Z" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M21.98 6.79001V10.79C21.98 13.73 20.63 15.31 17.94 15.54C17.97 15.3 17.98 15.05 17.98 14.79V10.79C17.98 7.60001 16.38 6 13.19 6H6.79004C6.53004 6 6.28004 6.01001 6.04004 6.04001C6.27004 3.35001 7.86004 2 10.79 2H17.19C20.38 2 21.98 3.60001 21.98 6.79001Z" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M13.4955 13.25H13.5045" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M9.9955 13.25H10.0045" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M6.4955 13.25H6.5045" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </g>
            </svg>
          </Box>
        ) : (
          <Box
            component="img"
            src={NoChannelIllustration}
            alt=""
            sx={{ width: { xs: 150, sm: 180 }, height: "auto", mb: 1.5 }}
          />
        )}
        {/* Same title/description scale as the Delete Contact dialog
            (InboxAll.jsx's convention) — "our new font sizing and weight". */}
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: { xs: "0.9375rem", sm: "1rem", md: "1.05rem", lg: "1.05rem", xl: "1.05rem" },
            color: "text.primary",
            mb: 0.5,
          }}
        >
          {isChannelConnected ? "No conversation selected" : "No channel connected"}
        </Typography>
        <Typography sx={{ fontSize: "0.875rem", color: "text.secondary", textAlign: "center", mb: 2 }}>
          {isChannelConnected
            ? "Select a conversation from the list to view messages."
            : "Connect a channel to start receiving messages here."}
        </Typography>
        {!isChannelConnected && (
          <Button
            variant="contained"
            onClick={() => {
              dispatch(setSelectedOption("Integrations"));
              navigate(`/home/${accountid}/settings`);
            }}
            sx={{
              px: 2.5,
              py: 1,
              backgroundColor: (theme) => (theme.palette.mode === "dark" ? "#4FC389" : "#44B57C"),
              color: "#ffffff",
              borderRadius: "12px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.9rem",
              fontFamily: "Instrument Sans, sans-serif",
              boxShadow: (theme) => (theme.palette.mode === "dark" ? "none" : "0 2px 8px rgba(68,181,124,0.25)"),
              transition: "all 0.3s ease",
              "&:hover": {
                backgroundColor: (theme) => (theme.palette.mode === "dark" ? "#3aad77" : "#38a06c"),
                boxShadow: (theme) => (theme.palette.mode === "dark" ? "none" : "0 8px 20px rgba(68,181,124,0.35)"),
              },
            }}
          >
            Connect Channel
          </Button>
        )}
      </Box>
    );
  }


  // effectiveContactId: use expectedContactId as fallback for the first click,
  // where selectedContactID (and therefore contactId) is still null because
  // setselectedContactID only fires after the fetch completes.
  const effectiveContactId = contactId || expectedContactId || null;

  // Show skeleton while loading, for any selected contact.
  // Uses effectiveContactId so skeleton shows even on first click (contactId=null)
  // and for contacts past page 1 of paginated inbox (contactExists may be false).
  const shouldShowLoading =
    !isTeamMemberChat && !!effectiveContactId && loading && (
      chats.length === 0 ||
      !messages ||
      (messages && messages.contactID !== effectiveContactId)
    );
  
  // Hide chat only when the contact is genuinely gone (deleted/removed).
  // Do NOT hide when contactExists=false merely because the contact scrolled
  // past page 1 of the paginated inbox — if messages are loaded or loading,
  // the contact is real and the chat should stay visible.
  const messagesLoadedForContact =
    messages &&
    String(messages.contactID || messages.contactId || '') === String(contactId);
  if (!isTeamMemberChat && contactId && !contactExists && !messagesLoadedForContact && !loading) {
    return null;
  }

  // Debug: Check loading states (removed to prevent infinite re-renders)


  

  if (shouldShowLoading) {
    // Rebuilt to match ChatCard.jsx's actual current row structure (Rahim,
    // 2026-08-29: "the chat box one... doesn't use new layout") — the old
    // version was flat pill bubbles with no avatar at all, from before the
    // 2026-08-27 bubble-type redesign gave every row a leading avatar
    // (flipped to the right via row-reverse for fromMe — this app shows an
    // avatar on BOTH sides, not just incoming) and 14px pill radius. Each
    // skeleton row now mirrors that: avatar circle + bubble, same side logic.
    // Fixed px, not %, on purpose — the bubble column below is a shrink-to-fit
    // flex item (sized by its own content, no definite width of its own), so
    // a percentage width has nothing real to resolve against there.
    const skeletonRows = [
      { fromMe: true, widths: [220, 140] },
      { fromMe: false, widths: [260] },
      { fromMe: true, widths: [160, 200] },
      { fromMe: false, widths: [280, 120] },
      { fromMe: true, widths: [180] },
      { fromMe: false, widths: [240, 160] },
      { fromMe: true, widths: [220] },
      { fromMe: false, widths: [200] },
    ];
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          padding: "16px 20px",
          gap: 1.5,
          backgroundColor: (theme) => theme.palette.background.default,
          overflowY: "hidden",
        }}
      >
        {skeletonRows.map((row, rowIdx) => (
          <Box
            key={rowIdx}
            sx={{
              display: "flex",
              flexDirection: row.fromMe ? "row-reverse" : "row",
              alignItems: "flex-end",
              gap: 1,
            }}
          >
            <Skeleton
              variant="circular"
              width={29}
              height={29}
              sx={{ flexShrink: 0 }}
            />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: row.fromMe ? "flex-end" : "flex-start",
                gap: 0.5,
                maxWidth: "calc(100% - 90px)",
              }}
            >
              {row.widths.map((w, i) => (
                <Skeleton
                  key={i}
                  variant="rounded"
                  width={w}
                  height={36}
                  sx={{ borderRadius: "14px" }}
                />
              ))}
            </Box>
          </Box>
        ))}
      </Box>
    );
  }

  if (
    (!chats || chats.length === 0) &&
    messages &&
    messages.contactID === contactId &&
    !isTeamMemberChat
  ) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <Typography color="text.secondary" variant="h6">
          No messages to show
        </Typography>
      </Box>
    );
  }

  
  return (
    <Box
      key={`chat-${contactId}`}
      ref={chatContainerRef}
      sx={{
        padding: { xs: "8px 5px", sm: 0.75, md: 1, lg: 1.25, xl: 1 },
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      {/* Loading indicator for older messages at the top */}
      {isLoadingOlder && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: { xs: 0.75, sm: 1, md: 1.5, lg: 1.75, xl: 1.25 },
            gap: { xs: 0.75, sm: 1, md: 1.5, lg: 1.75, xl: 1.25 },
            backgroundColor: (theme) => theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.02)",
            borderRadius: 1,
            marginBottom: { xs: 0.75, sm: 1, md: 1.5, lg: 1.75, xl: 1.25 },
          }}
        >
          <CircularProgress size={20} thickness={4} />
          <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.5625rem', sm: '0.625rem', md: '0.75rem', lg: '0.75rem', xl: '0.75rem' }, fontWeight: 400 }}>
            Loading older messages...
          </Typography>
        </Box>
      )}
      
      {/* Loading indicator for targeted message in center */}
      {isLoadingTargetMessage && (
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 1000,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: { xs: 0.75, sm: 1, md: 1.5, lg: 1.75, xl: 1.25 },
            backgroundColor: (theme) => theme.palette.mode === "dark" ? "rgba(0, 0, 0, 0.8)" : "rgba(255, 255, 255, 0.9)",
            padding: { xs: 1.5, sm: 2, md: 2.5, lg: 2.75, xl: 2 },
            borderRadius: 2,
            boxShadow: 3,
          }}
        >
          <CircularProgress size={32} />
          <Typography variant="body1" color="text.primary" fontWeight={500} sx={{ fontSize: { xs: '0.6875rem', sm: '0.75rem', md: '0.875rem', lg: '0.875rem', xl: '0.875rem' } }}>
            Loading message...
          </Typography>
        </Box>
      )}
      
      {/* All messages rendered at once - browser handles virtualization */}
      {groupedChats.map((msg, index) => {
          if (msg.type === "catalog" || (msg.type === "chat" && msg.messageType === "catalog") || (() => {
            // Fallback: Detect catalog messages from JSON body structure
            // This handles cases where type/messageType might not be set correctly
            try {
              const jsonString = msg.body || msg.message;
              if (jsonString && typeof jsonString === 'string') {
                const parsed = JSON.parse(jsonString);
                if (parsed && (parsed.catalogType || parsed.productData)) {
                  return true; // This is a catalog message
                }
              }
            } catch (e) {
              // Not JSON or not catalog structure
            }
            return false;
          })()) {
            // Catalog now renders through ChatCard's shared bubble scaffold (pill shape,
            // grouping, side avatar, date/tick) instead of a custom-built row here — same
            // move as template (Rahim, 2026-08-25: "render it under the template chat...
            // each and every case").
            const catalogDisplayNames = getDisplayNames(msg);
            const uniqueKey = `catalog-${index}-${msg.id || msg._id || msg.tempId || 'no-id'}`;
            const isCatalogGroupStart = !isSamePillGroup(groupedChats[index - 1], msg);
            const isCatalogGroupEnd = !isSamePillGroup(msg, groupedChats[index + 1]);
            return (
              <Box
                sx={{ maxWidth: "100%" }}
                key={uniqueKey}
                ref={(el) => {
                  messageRefs.current[msg.id] = el;
                }}
              >
                <ChatCard
                  isTeamMemberChat={isTeamMemberChat}
                  sendername={catalogDisplayNames.sendername}
                  clientname={catalogDisplayNames.clientname}
                  time={getMessageTimestamp(msg)}
                  message={msg.body || msg.message}
                  messageType="catalog"
                  status={msg.status}
                  onReplyClick={handleScrollToMessage}
                  reaction={msg.reaction}
                  isInternalMention={msg.isInternalMention}
                  senderAvatarColor={msg.senderAvatarColor}
                  messagePlatform={getMessageBadgePlatform(msg, messages?.platform)}
                  pillGrouping
                  isGroupStart={isCatalogGroupStart}
                  isGroupEnd={isCatalogGroupEnd}
                />
              </Box>
            );
          } else if (msg.type === "chat" && msg.messageType === "list") {
            // Same move as catalog/template: renders through ChatCard's shared bubble
            // scaffold instead of a custom row built here.
            const listDisplayNames = getDisplayNames(msg);
            const uniqueKey = `list-${index}-${msg.id || msg._id || msg.tempId || 'no-id'}`;
            const isListGroupStart = !isSamePillGroup(groupedChats[index - 1], msg);
            const isListGroupEnd = !isSamePillGroup(msg, groupedChats[index + 1]);
            return (
              <Box
                sx={{ maxWidth: "100%" }}
                key={uniqueKey}
                ref={(el) => {
                  messageRefs.current[msg.id] = el;
                }}
              >
                <ChatCard
                  isTeamMemberChat={isTeamMemberChat}
                  sendername={listDisplayNames.sendername}
                  clientname={listDisplayNames.clientname}
                  time={getMessageTimestamp(msg)}
                  message={msg.body || msg.message}
                  messageType="list"
                  status={msg.status}
                  onReplyClick={handleScrollToMessage}
                  reaction={msg.reaction}
                  isInternalMention={msg.isInternalMention}
                  senderAvatarColor={msg.senderAvatarColor}
                  messagePlatform={getMessageBadgePlatform(msg, messages?.platform)}
                  pillGrouping
                  isGroupStart={isListGroupStart}
                  isGroupEnd={isListGroupEnd}
                />
              </Box>
            );
          } else if (msg.type === "chat") {
            // CRITICAL: Check if this is actually a catalog message that wasn't caught above
            // This is a fallback to prevent catalog messages from being rendered as text
            let isCatalogMessage = false;
            try {
              const jsonString = msg.body || msg.message;
              if (jsonString && typeof jsonString === 'string') {
                const parsed = JSON.parse(jsonString);
                if (parsed && (parsed.catalogType || parsed.productData)) {
                  isCatalogMessage = true;
                }
              }
            } catch (e) {
              // Not JSON or not catalog structure
            }
            
            // If it's a catalog message, render it as catalog — same ChatCard delegation
            // as the dedicated catalog branch above.
            if (isCatalogMessage) {
              const catalogFallbackDisplayNames = getDisplayNames(msg);
              const uniqueKey = `catalog-${index}-${msg.id || msg._id || msg.tempId || 'no-id'}`;
              const isCatalogFallbackGroupStart = !isSamePillGroup(groupedChats[index - 1], msg);
              const isCatalogFallbackGroupEnd = !isSamePillGroup(msg, groupedChats[index + 1]);
              return (
                <Box
                  sx={{ maxWidth: "100%" }}
                  key={uniqueKey}
                  ref={(el) => {
                    messageRefs.current[msg.id] = el;
                  }}
                >
                  <ChatCard
                    isTeamMemberChat={isTeamMemberChat}
                    sendername={catalogFallbackDisplayNames.sendername}
                    clientname={catalogFallbackDisplayNames.clientname}
                    time={getMessageTimestamp(msg)}
                    message={msg.body || msg.message}
                    messageType="catalog"
                    status={msg.status}
                    onReplyClick={handleScrollToMessage}
                    reaction={msg.reaction}
                    isInternalMention={msg.isInternalMention}
                    senderAvatarColor={msg.senderAvatarColor}
                    messagePlatform={getMessageBadgePlatform(msg, messages?.platform)}
                    pillGrouping
                    isGroupStart={isCatalogFallbackGroupStart}
                    isGroupEnd={isCatalogFallbackGroupEnd}
                  />
                </Box>
              );
            }
            
            // Removed debug logging to prevent infinite re-renders
            // Debug logging was causing performance issues
            // CRITICAL: For Facebook/Instagram media, prefer url so image/video/audio/document display correctly
            const isMediaType = msg.messageType && ["image", "video", "audio", "document"].includes(msg.messageType);
            // imageCollage/videoCollage's `message` is a real array (MediaCollage indexes it
            // directly) — without this it fell into the plain-text branch below, which
            // `String(...)`-coerces an array to a comma-joined string and breaks the
            // component (Rahim, 2026-08-27: found while making these types visible for the
            // first time — no mock data had ever exercised this path before).
            const isCollageType = msg.messageType === "imageCollage" || msg.messageType === "videoCollage";
            const isLocationType = msg.messageType === "location";
            const isContactsType = msg.messageType === "contacts";
            // Pill-grouping: every "chat"-type bubble — plain text, image/video/audio/document,
            // location/contacts, and now imageCollage/videoCollage too (Rahim, 2026-08-27:
            // "you still havent change the time and name... using from previous ui" — was
            // deliberately excluded while the collage component itself was still broken/
            // untested; now that it's fixed, same treatment as everything else) — shares the
            // same spacing/padding/tick-icon baseline as catalog/list/template. The outer
            // bubble chrome this flag controls is owned by ChatCard itself, not the inner
            // media components, so anything left out of this list is stuck on the
            // pre-redesign layout regardless of how good its own content looks.
            // "template" added 2026-08-31: a template message can also arrive here as
            // msg.type==="chat" + msg.messageType==="template" (the dedicated msg.type==="template"
            // branch below already got pillGrouping — this branch was the one silently missed,
            // same "left out of the list" trap as collages before).
            const isPlainPillType =
              !msg.messageType ||
              isCollageType ||
              ["text", "chat", "image", "video", "audio", "document", "location", "contacts", "template"].includes(msg.messageType);
            const isChatGroupStart = !isSamePillGroup(groupedChats[index - 1], msg);
            const isChatGroupEnd = !isSamePillGroup(msg, groupedChats[index + 1]);
            const messageContent = (isMediaType || isCollageType) && (msg.url || msg.message)
              ? (msg.url || msg.message)
              : isLocationType || isContactsType
                ? ((msg.body && String(msg.body).trim()) || (msg.message && String(msg.message).trim()) || "")
                : ((msg.message && String(msg.message).trim()) || (msg.body && String(msg.body).trim()) || "");
            // CRITICAL: Ensure we have a stable, unique key for React by combining index with message ID
            // This prevents duplicate key warnings when messages have the same ID
            // CRITICAL: Include sendername and status in key to force re-render when message is updated
            // This ensures React detects changes when internal mentions are updated with server data
            const messageKey = `chat-${index}-${msg.id || msg._id || msg.tempId || `msg-${index}-${msg.timestamp || Date.now()}`}-${msg.sendername || 'no-sender'}-${msg.status || 'no-status'}`;
            return (
              <Box
                sx={{ maxWidth: "100%" }}
                key={messageKey}
                ref={(el) => {
                  if (msg.id || msg._id || msg.tempId) {
                    messageRefs.current[msg.id || msg._id || msg.tempId] = el;
                  }
                }}
              >
                <ChatCard
                  isTeamMemberChat={isTeamMemberChat}
                  sendername={getDisplayNames(msg).sendername}
                  clientname={getDisplayNames(msg).clientname}
                  time={getMessageTimestamp(msg)}
                  message={messageContent}
                  messageType={msg.messageType || "text"}
                  caption={msg.caption}
                  replyBody={msg.replyBody}
                  replyType={msg.replyType}
                  replyId={msg.replyId}
                  status={msg.status}
                  onReplyClick={handleScrollToMessage}
                  replySenderName={msg.replySenderName}
                  reaction={msg.reaction}
                  isInternalMention={msg.isInternalMention}
                  senderAvatarColor={msg.senderAvatarColor}
                  audioDurationSeconds={msg.audioDurationSeconds}
                  mediaThumbnailUrl={msg.mediaThumbnailUrl}
                  mediaFileSizeBytes={msg.mediaFileSizeBytes}
                  documentFileName={msg.documentFileName || msg.filename}
                  messagePlatform={getMessageBadgePlatform(msg, messages?.platform)}
                  contactId={contactId}
                  pillGrouping={isPlainPillType}
                  isGroupStart={isChatGroupStart}
                  isGroupEnd={isChatGroupEnd}
                  messageId={msg.id || msg._id || msg.tempId}
                  metaMediaId={msg.metaMediaId}
                  // A failed QR message (a number's chat or a group) shows only "Failed to send" on hover: the real reason stays saved on the
                  // message (admin panel) but is not shown to the user. Every other channel keeps showing its reason.
                  errorMessage={msg.platform === "qr" || msg.waConnectionId || isGroupContact ? undefined : msg.errorMessage}
                />
              </Box>
            );
          } else if (msg.type === "date") {
            const formattedDate = formatDateSeparator(msg.timestamp);
            // Use index to ensure unique keys even if timestamps are duplicated
            const uniqueKey = `date-${index}-${msg.timestamp || Date.now()}`;
            return (
              <Box
                key={uniqueKey}
                ref={(el) => (messageRefs.current[`date-${getMessageTimestamp(msg) || Date.now()}`] = el)}
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "100%",
                  marginY: 3,
                }}
              >
                <Box
                  sx={(theme) => ({
                    // Grey chip, squared-off corners — not a white pill
                    // (Rahim, 2026-08-24: "use square corner radius corners
                    // with nota white background but a grey type one").
                    // grey[200] (#eeeeee) was nearly indistinguishable from
                    // the chat pane's own background.default (#f5f5f5) —
                    // bumped a step darker/lighter each way for real contrast
                    // (Rahim, 2026-08-24: "change the background of date so
                    // its more visisble").
                    backgroundColor: theme.palette.mode === "dark" ? theme.palette.grey[700] : theme.palette.grey[300],
                    color: theme.palette.mode === "dark" ? theme.palette.grey[200] : theme.palette.grey[700],
                    padding: "10px 12px",
                    borderRadius: "6px",
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    textAlign: "center",
                    minWidth: "80px",
                  })}
                >
                  {formattedDate}
                </Box>
              </Box>
            );
          } else if (msg.type === "group_created") {
            // Group event system-log types (2026-09-26) -- centered pills, same ChatLog
            // mechanism as assignment/closechat/blockcontact/call just below, only new `type`
            // values. Worded as "now in your inbox," not "group created": this fires the first
            // time ClusterDen ever processes a message from a group, which can't be told apart
            // from an old WhatsApp group ClusterDen simply never tracked before (see
            // baileysGroupInboundHandler.js's own doc comment on this).
            let subject = "";
            let addedBy = "";
            try {
              const parsedCreated = JSON.parse(msg.body || "{}");
              subject = parsedCreated?.subject || "";
              addedBy = parsedCreated?.addedBy || "";
            } catch (_) { /* not JSON, ignore */ }
            const formattedMessage = addedBy ? (
              <><Emph>{addedBy}</Emph> added you to the group {subject ? <Emph>{subject}</Emph> : null} at {formatTime(msg.timestamp)}</>
            ) : subject ? (
              <>This group — <Emph>{subject}</Emph> — is now in your inbox</>
            ) : (
              <>This group is now in your inbox</>
            );
            const uniqueKey = `group-created-${index}-${msg.id || msg._id || msg.tempId || 'no-id'}`;
            return (
              <div key={uniqueKey} ref={(el) => (messageRefs.current[msg.id] = el)}>
                <ChatLog icon={<People size="16" variant="Linear" color="currentColor" />} message={formattedMessage} />
              </div>
            );
          } else if (msg.type === "group_participants_update") {
            let parsed = null;
            try { parsed = JSON.parse(msg.body || "{}"); } catch (_) { /* not JSON, ignore */ }
            const participantAction = parsed?.participantAction;
            const participants = Array.isArray(parsed?.participants) ? parsed.participants : [];
            const author = parsed?.author;
            const nameOf = (p) => p?.name || p?.phoneNumberID || p?.jid || "someone";
            const authorName = author?.name || author?.phoneNumberID || null;
            const namesText = participants.length > 0 ? participants.map(nameOf).join(", ") : "someone";
            const verb =
              participantAction === "add" ? "added" :
              participantAction === "remove" ? "removed" :
              participantAction === "promote" ? "made group admin" :
              participantAction === "demote" ? "removed as group admin" : "updated";
            // Someone removing THEMSELVES is a leave ("You left", like WhatsApp), not "X removed X".
            const isSelfLeave =
              participantAction === "remove" &&
              participants.length === 1 &&
              !!author?.phoneNumberID &&
              participants[0]?.phoneNumberID === author.phoneNumberID;
            const formattedMessage = isSelfLeave ? (
              <><Emph>{authorName}</Emph> left at {formatTime(msg.timestamp)}</>
            ) : authorName ? (
              <><Emph>{authorName}</Emph> {verb} <Emph>{namesText}</Emph> at {formatTime(msg.timestamp)}</>
            ) : (
              <><Emph>{namesText}</Emph> {participantAction === "remove" ? "left or was removed" : verb} at {formatTime(msg.timestamp)}</>
            );
            const isRemovalLike = participantAction === "remove" || participantAction === "demote";
            const uniqueKey = `group-participants-${index}-${msg.id || msg._id || msg.tempId || 'no-id'}`;
            return (
              <div key={uniqueKey} ref={(el) => (messageRefs.current[msg.id] = el)}>
                <ChatLog
                  icon={isRemovalLike
                    ? <ProfileRemove size="16" variant="Linear" color="currentColor" />
                    : <ProfileTick size="16" variant="Linear" color="currentColor" />}
                  message={formattedMessage}
                />
              </div>
            );
          } else if (msg.type === "group_join_request") {
            // Someone asked to join (or cancelled / was turned down) in a group with admin approval
            // on -- worded like WhatsApp's own system lines. Approval itself shows as the normal
            // "X added Y" line.
            let parsed = null;
            try { parsed = JSON.parse(msg.body || "{}"); } catch (_) { /* not JSON, ignore */ }
            const who = parsed?.participant?.name || parsed?.participant?.phoneNumberID || "Someone";
            const byName = parsed?.author?.name || parsed?.author?.phoneNumberID || null;
            let formattedMessage;
            if (parsed?.requestAction === "revoked") {
              formattedMessage = <><Emph>{who}</Emph> cancelled their request to join this group at {formatTime(msg.timestamp)}</>;
            } else if (parsed?.requestAction === "rejected") {
              formattedMessage = byName
                ? <><Emph>{byName}</Emph> rejected <Emph>{who}</Emph>'s request to join this group at {formatTime(msg.timestamp)}</>
                : <><Emph>{who}</Emph>'s request to join this group was rejected at {formatTime(msg.timestamp)}</>;
            } else {
              formattedMessage = <><Emph>{who}</Emph> requested to join this group{parsed?.method === "invite_link" ? " via the invite link" : ""} at {formatTime(msg.timestamp)}</>;
            }
            const uniqueKey = `group-join-request-${index}-${msg.id || msg._id || msg.tempId || 'no-id'}`;
            return (
              <div key={uniqueKey} ref={(el) => (messageRefs.current[msg.id] = el)}>
                <ChatLog icon={<ProfileTick size="16" variant="Linear" color="currentColor" />} message={formattedMessage} />
              </div>
            );
          } else if (msg.type === "group_settings_update") {
            // Group SETTINGS change (who can add members / send messages / edit info / admin
            // approval to join) -- worded like WhatsApp's own system lines. Same ChatLog pill as
            // the other group event rows around it; only a new `type`.
            let parsed = null;
            try { parsed = JSON.parse(msg.body || "{}"); } catch (_) { /* not JSON, ignore */ }
            const authorName = parsed?.author?.name || parsed?.author?.phoneNumberID || "Someone";
            const value = parsed?.value;
            let formattedMessage;
            if (parsed?.setting === "joinApprovalMode") {
              formattedMessage = (
                <><Emph>{authorName}</Emph> turned {value ? "on" : "off"} admin approval to join this group at {formatTime(msg.timestamp)}</>
              );
            } else {
              const change =
                parsed?.setting === "memberAddMode"
                  ? (value === "all_member_add" ? "allow all members to add others to this group" : "allow only admins to add others to this group")
                  : parsed?.setting === "announce"
                    ? (value ? "allow only admins to send messages to this group" : "allow all members to send messages to this group")
                    : parsed?.setting === "restrict"
                      ? (value ? "allow only admins to edit this group's info" : "allow all members to edit this group's info")
                      : "a group setting";
              formattedMessage = (
                <><Emph>{authorName}</Emph> changed this group's settings to {change} at {formatTime(msg.timestamp)}</>
              );
            }
            const uniqueKey = `group-settings-${index}-${msg.id || msg._id || msg.tempId || 'no-id'}`;
            return (
              <div key={uniqueKey} ref={(el) => (messageRefs.current[msg.id] = el)}>
                <ChatLog icon={<Edit2 size="16" variant="Linear" color="currentColor" />} message={formattedMessage} />
              </div>
            );
          } else if (msg.type === "group_subject_update" || msg.type === "group_description_update") {
            let parsed = null;
            try { parsed = JSON.parse(msg.body || "{}"); } catch (_) { /* not JSON, ignore */ }
            const authorName = parsed?.author?.name || parsed?.author?.phoneNumberID || "Someone";
            const formattedMessage = msg.type === "group_subject_update" ? (
              <><Emph>{authorName}</Emph> changed the group name to <Emph>{parsed?.subject || ""}</Emph> at {formatTime(msg.timestamp)}</>
            ) : (
              <><Emph>{authorName}</Emph> changed the group description at {formatTime(msg.timestamp)}</>
            );
            const uniqueKey = `group-metadata-${index}-${msg.id || msg._id || msg.tempId || 'no-id'}`;
            return (
              <div key={uniqueKey} ref={(el) => (messageRefs.current[msg.id] = el)}>
                <ChatLog icon={<Edit2 size="16" variant="Linear" color="currentColor" />} message={formattedMessage} />
              </div>
            );
          } else if (msg.type === "assignment") {
            // Check if this is an unassignment message
            // Either to contains "assignment" (case-insensitive), or both to and toName are null/undefined
            const isUnassignment = 
              (msg.to && (msg.to.toLowerCase().includes("assignment") || msg.to === "assignment" || msg.to === "Assignment")) ||
              (!msg.to && !msg.toName) ||
              (msg.body && msg.body.toLowerCase() === "unassigned");
            
            const isWorkflowAssignment = !msg.performedBy || msg.performedBy === "workflow";
            const assignedToName = msg.toName || msg.to || "Unknown";
            const formattedMessage = isUnassignment ? (
              `Marked Unassigned automatically at ${formatTime(msg.date)}`
            ) : isWorkflowAssignment ? (
              <>Assigned to <Emph>{assignedToName}</Emph> automatically at {formatTime(msg.date)}</>
            ) : (
              <>
                Assigned to <Emph>{assignedToName}</Emph> by <Emph>{msg.performedByName || "Team Member"}</Emph> at {formatTime(msg.date)}
              </>
            );
            const assignmentIcon = isUnassignment ? (
              <ProfileRemove size="16" variant="Linear" color="currentColor" />
            ) : (
              <ProfileTick size="16" variant="Linear" color="currentColor" />
            );
            // Use index to ensure unique keys even if message IDs are duplicated
            const uniqueKey = `assignment-${index}-${msg.id || msg._id || msg.tempId || 'no-id'}`;
            return (
              <div key={uniqueKey} ref={(el) => (messageRefs.current[msg.id] = el)}>
                <ChatLog icon={assignmentIcon} message={formattedMessage} />
              </div>
            );
          } else if (msg.type === "call") {
            // Prefer `date` (what the REST fetch always populates for call rows) but
            // fall back to the generic timestamp fields every message type carries —
            // same fallback chain groupConsecutiveMessages already uses above via
            // getMessageTimestamp. Without this, a live socket-appended call bubble
            // could render "at -" if `date` didn't happen to survive that specific
            // hop, even though timestamp/time/createdAt always do.
            const at = `at ${formatTime(msg.date || getMessageTimestamp(msg))}`;
            // The server (calling.webhook.js's formatCallSummary /
            // createTransferChatEntry) already has the full picture — status,
            // duration, which agent, and now transfer notifications too — and
            // computes the exact text once, in one place. Re-deriving it here
            // from raw callStatus/callDirection was itself the bug: a transfer
            // notification isn't a "call ended" event, so it carries neither
            // field, and silently fell through to a generic "failed" message
            // that had nothing to do with what actually happened. body is the
            // single source of truth going forward; the field-based
            // reconstruction only remains as a fallback for messages saved
            // before body was consistently populated.
            let formattedMessage;
            // Icon is chosen from the same callStatus/direction the text is
            // built from, instead of an emoji baked into the string — the
            // emoji prefixes below are gone, replaced by an actual <ChatLog
            // icon> element (Rahim, 2026-08-24: "simple text and icon we
            // will be using our new icon").
            let callIcon = <CallIcon size="16" variant="Linear" color="currentColor" />;
            if (msg.body) {
              // Legacy/server-formatted bodies may still carry a leading
              // emoji from before this pass — strip it so we don't end up
              // with "✅ Outbound call..." next to the new icon too.
              const strippedBody = msg.body.replace(/^\p{Extended_Pictographic}️?\s*/u, "");
              formattedMessage = `${strippedBody} ${at}`;
              if (msg.callStatus === "completed") callIcon = <TickCircle size="16" variant="Linear" color="currentColor" />;
              else if (msg.callStatus === "missed") callIcon = <CallSlash size="16" variant="Linear" color="currentColor" />;
              else if (msg.callStatus === "abandoned" || msg.callStatus === "declined") callIcon = <CallRemove size="16" variant="Linear" color="currentColor" />;
              else if (msg.callStatus === "ringing" || msg.callStatus === "connecting")
                callIcon = msg.callDirection === "outbound" ? (
                  <CallOutgoing size="16" variant="Linear" color="currentColor" />
                ) : (
                  <CallIncoming size="16" variant="Linear" color="currentColor" />
                );
              else if (!msg.callStatus) callIcon = <CallSlash size="16" variant="Linear" color="currentColor" />;
            } else {
              const direction = msg.callDirection === "outbound" ? "Outbound call" : "Incoming call";
              const agent = msg.performedByName || "agent";
              // Sub-minute calls read as "11s", not "0m 11s" — the "0m" prefix
              // looks like a rounding artifact rather than a real duration.
              const secs = msg.callDurationSeconds || 0;
              const duration = secs < 60 ? `${secs}s` : `${Math.floor(secs / 60)}m ${secs % 60}s`;
              if (msg.callStatus === "completed") {
                formattedMessage =
                  msg.callDirection === "outbound" ? (
                    <>{direction} by <Emph>{agent}</Emph> — {duration} {at}</>
                  ) : (
                    <>{direction} — accepted by <Emph>{agent}</Emph> — {duration} {at}</>
                  );
                callIcon = <TickCircle size="16" variant="Linear" color="currentColor" />;
              } else if (msg.callStatus === "missed") {
                formattedMessage = `${direction} — missed ${at}`;
                callIcon = <CallSlash size="16" variant="Linear" color="currentColor" />;
              } else if (msg.callStatus === "abandoned") {
                formattedMessage = `${direction} — caller disconnected before answer ${at}`;
                callIcon = <CallRemove size="16" variant="Linear" color="currentColor" />;
              } else if (msg.callStatus === "declined") {
                formattedMessage =
                  msg.callDirection === "outbound" ? (
                    <>{direction} by <Emph>{agent}</Emph> — declined {at}</>
                  ) : (
                    <>{direction} — declined by <Emph>{agent}</Emph> {at}</>
                  );
                callIcon = <CallRemove size="16" variant="Linear" color="currentColor" />;
              } else if (msg.callStatus === "ringing" || msg.callStatus === "connecting") {
                formattedMessage = `${direction}… ${at}`;
                callIcon = msg.callDirection === "outbound" ? (
                  <CallOutgoing size="16" variant="Linear" color="currentColor" />
                ) : (
                  <CallIncoming size="16" variant="Linear" color="currentColor" />
                );
              } else {
                formattedMessage = `${direction} — failed ${at}`;
                callIcon = <CallSlash size="16" variant="Linear" color="currentColor" />;
              }
            }
            const uniqueKey = `call-${index}-${msg.id || msg._id || msg.tempId || 'no-id'}`;
            return (
              <div key={uniqueKey} ref={(el) => (messageRefs.current[msg.id] = el)}>
                <ChatLog icon={callIcon} message={formattedMessage} />
              </div>
            );
          } else if (msg.type === "closechat") {
            const timeValue = msg.timestamp || msg.date || null;
            const closePerformer =
              msg.performedBy === "teamMember" && msg.performedByName
                ? msg.performedByName
                : "workflow";
            const formattedMessage = (
              <>Chat Closed by <Emph>{closePerformer}</Emph> at {formatTime(timeValue)}</>
            );
            // Use index to ensure unique keys even if message IDs are duplicated
            const uniqueKey = `closechat-${index}-${msg.id || msg._id || msg.tempId || 'no-id'}`;
            return (
              <div key={uniqueKey} ref={(el) => (messageRefs.current[msg.id] = el)}>
                <ChatLog icon={<TickCircle size="16" variant="Linear" color="currentColor" />} message={formattedMessage} />
              </div>
            );
          } else if (msg.type === "blockcontact") {
            const timeValue = msg.timestamp || msg.date || null;
            const blockPerformer =
              msg.performedBy === "teamMember" && msg.performedByName
                ? msg.performedByName
                : "workflow";
            const isUnblockMsg = msg.body && msg.body.toLowerCase() === "unblocked";
            const formattedMessage = isUnblockMsg ? (
              <>Contact Unblocked by <Emph>{blockPerformer}</Emph> at {formatTime(timeValue)}</>
            ) : (
              <>Contact Blocked by <Emph>{blockPerformer}</Emph> at {formatTime(timeValue)}</>
            );
            // Use index to ensure unique keys even if message IDs are duplicated
            const uniqueKey = `blockcontact-${index}-${msg.id || msg._id || msg.tempId || 'no-id'}`;
            return (
              <div key={uniqueKey} ref={(el) => (messageRefs.current[msg.id] = el)}>
                <ChatLog
                  icon={
                    isUnblockMsg ? (
                      <Unlock size="16" variant="Linear" color="currentColor" />
                    ) : (
                      <Forbidden2 size="16" variant="Linear" color="currentColor" />
                    )
                  }
                  message={formattedMessage}
                />
              </div>
            );
          } else if (msg.type?.startsWith("shopify_")) {
            // Covers every Shopify event card type (order placed/cancelled/paid,
            // checkout created, shipped, fulfilled, refunded, returns, customer
            // synced, etc.) — every one of them shares the "shopify_" prefix, so
            // one check covers all of them instead of an ever-growing OR list.
            // Server builds the human-readable line (see insertShopifyEventCard
            // in shopifyOrderMessagingService.js); the timestamp is appended
            // client-side the same way closechat/blockcontact/call do it above,
            // so a merchant can see exactly when each event happened.
            const shopifyTimeValue = msg.timestamp || msg.date || null;
            const formattedShopifyMessage = `${msg.body} at ${formatTime(shopifyTimeValue)}`;
            const uniqueKey = `shopify-${index}-${msg.id || msg._id || msg.tempId || 'no-id'}`;
            return (
              <div key={uniqueKey} ref={(el) => (messageRefs.current[msg.id] = el)}>
                <ChatLog message={formattedShopifyMessage} fontSize="0.95rem" />
              </div>
            );
          } else if (msg.type === "story_reply") {
            // Instagram reply to story – show like Instagram (story preview + reply text)
            const uniqueKey = `story_reply-${index}-${msg.id || msg._id || msg.tempId || 'no-id'}`;
            const storyDisplayNames = getDisplayNames(msg);
            const isStoryGroupStart = !isSamePillGroup(groupedChats[index - 1], msg);
            const isStoryGroupEnd = !isSamePillGroup(msg, groupedChats[index + 1]);
            return (
              <Box
                sx={{ maxWidth: "100%" }}
                key={uniqueKey}
                ref={(el) => {
                  messageRefs.current[msg.id] = el;
                }}
              >
                <ChatCard
                  isTeamMemberChat={isTeamMemberChat}
                  sendername={storyDisplayNames.sendername}
                  clientname={storyDisplayNames.clientname}
                  time={getMessageTimestamp(msg)}
                  message={msg.message || msg.body}
                  messageType="story_reply"
                  storyUrl={msg.storyUrl}
                  storyId={msg.storyId}
                  storyReplyAvatarUrl={msg.storyReplyAvatarUrl}
                  status={msg.status}
                  onReplyClick={handleScrollToMessage}
                  reaction={msg.reaction}
                  pillGrouping
                  isGroupStart={isStoryGroupStart}
                  isGroupEnd={isStoryGroupEnd}
                />
              </Box>
            );
          } else if (msg.type === "post_comment" && (messages?.platform === "instagram" || messages?.platform === "facebook")) {
            // Instagram/Facebook post comment – show with optional post preview
            const uniqueKey = `post_comment-${index}-${msg.id || msg._id || msg.tempId || 'no-id'}`;
            const postCommentDisplayNames = getDisplayNames(msg);
            const isPostCommentGroupStart = !isSamePillGroup(groupedChats[index - 1], msg);
            const isPostCommentGroupEnd = !isSamePillGroup(msg, groupedChats[index + 1]);
            return (
              <Box
                sx={{ maxWidth: "100%" }}
                key={uniqueKey}
                ref={(el) => {
                  messageRefs.current[msg.id] = el;
                }}
              >
                <ChatCard
                  isTeamMemberChat={isTeamMemberChat}
                  sendername={postCommentDisplayNames.sendername}
                  clientname={postCommentDisplayNames.clientname}
                  time={getMessageTimestamp(msg)}
                  message={msg.message || msg.body}
                  messageType="post_comment"
                  status={msg.status}
                  onReplyClick={handleScrollToMessage}
                  postMediaUrl={msg.postMediaUrl}
                  postPermalink={msg.postPermalink}
                  postMediaType={msg.postMediaType}
                  reaction={msg.reaction}
                  pillGrouping
                  isGroupStart={isPostCommentGroupStart}
                  isGroupEnd={isPostCommentGroupEnd}
                />
              </Box>
            );
          } else if (msg.type === "ctwa_referral") {
            // Click-to-WhatsApp Ads – first message of a session started from an ad/post click
            const uniqueKey = `ctwa_referral-${index}-${msg.id || msg._id || msg.tempId || 'no-id'}`;
            const ctwaDisplayNames = getDisplayNames(msg);
            const isCtwaGroupStart = !isSamePillGroup(groupedChats[index - 1], msg);
            const isCtwaGroupEnd = !isSamePillGroup(msg, groupedChats[index + 1]);
            return (
              <Box
                sx={{ maxWidth: "100%" }}
                key={uniqueKey}
                ref={(el) => {
                  messageRefs.current[msg.id] = el;
                }}
              >
                <ChatCard
                  isTeamMemberChat={isTeamMemberChat}
                  sendername={ctwaDisplayNames.sendername}
                  clientname={ctwaDisplayNames.clientname}
                  time={getMessageTimestamp(msg)}
                  message={msg.message || msg.body}
                  messageType="ctwa_referral"
                  status={msg.status}
                  onReplyClick={handleScrollToMessage}
                  referralSourceType={msg.referralSourceType}
                  referralHeadline={msg.referralHeadline}
                  referralBody={msg.referralBody}
                  referralMediaType={msg.referralMediaType}
                  referralMediaUrl={msg.referralMediaUrl}
                  reaction={msg.reaction}
                  pillGrouping
                  isGroupStart={isCtwaGroupStart}
                  isGroupEnd={isCtwaGroupEnd}
                />
              </Box>
            );
          } else if (msg.type === "live_comment" && messages?.platform === "instagram") {
            // Instagram live comment – show only for Instagram contacts
            const uniqueKey = `live_comment-${index}-${msg.id || msg._id || msg.tempId || 'no-id'}`;
            const liveCommentDisplayNames = getDisplayNames(msg);
            const isLiveCommentGroupStart = !isSamePillGroup(groupedChats[index - 1], msg);
            const isLiveCommentGroupEnd = !isSamePillGroup(msg, groupedChats[index + 1]);
            return (
              <Box
                sx={{ maxWidth: "100%" }}
                key={uniqueKey}
                ref={(el) => {
                  messageRefs.current[msg.id] = el;
                }}
              >
                <ChatCard
                  isTeamMemberChat={isTeamMemberChat}
                  sendername={liveCommentDisplayNames.sendername}
                  clientname={liveCommentDisplayNames.clientname}
                  time={getMessageTimestamp(msg)}
                  message={msg.message || msg.body}
                  messageType="live_comment"
                  status={msg.status}
                  onReplyClick={handleScrollToMessage}
                  reaction={msg.reaction}
                  pillGrouping
                  isGroupStart={isLiveCommentGroupStart}
                  isGroupEnd={isLiveCommentGroupEnd}
                />
              </Box>
            );
          } else if (msg.type === "instagram_reaction" && messages?.platform === "instagram") {
            // Instagram reaction – preview of message that was reacted to + reaction emoji (same layout as story reply / post comment)
            const uniqueKey = `instagram_reaction-${index}-${msg.id || msg._id || msg.tempId || "no-id"}`;
            const reactionDisplayNames = getDisplayNames(msg);
            const isIgReactionGroupStart = !isSamePillGroup(groupedChats[index - 1], msg);
            const isIgReactionGroupEnd = !isSamePillGroup(msg, groupedChats[index + 1]);
            return (
              <Box
                sx={{ maxWidth: "100%" }}
                key={uniqueKey}
                ref={(el) => {
                  if (msg.id) messageRefs.current[msg.id] = el;
                }}
              >
                <ChatCard
                  isTeamMemberChat={isTeamMemberChat}
                  sendername={reactionDisplayNames.sendername}
                  clientname={reactionDisplayNames.clientname}
                  time={getMessageTimestamp(msg)}
                  message={msg.message || msg.body}
                  messageType="instagram_reaction"
                  status={msg.status}
                  onReplyClick={handleScrollToMessage}
                  reaction={msg.reaction}
                  reactionPreview={msg.reactionPreview}
                  pillGrouping
                  isGroupStart={isIgReactionGroupStart}
                  isGroupEnd={isIgReactionGroupEnd}
                />
              </Box>
            );
          } else if (msg.type === "facebook_reaction" && messages?.platform === "facebook") {
            // Facebook reaction – same layout as Instagram reaction
            const uniqueKey = `facebook_reaction-${index}-${msg.id || msg._id || msg.tempId || "no-id"}`;
            const reactionDisplayNames = getDisplayNames(msg);
            const isFbReactionGroupStart = !isSamePillGroup(groupedChats[index - 1], msg);
            const isFbReactionGroupEnd = !isSamePillGroup(msg, groupedChats[index + 1]);
            return (
              <Box
                sx={{ maxWidth: "100%" }}
                key={uniqueKey}
                ref={(el) => {
                  if (msg.id) messageRefs.current[msg.id] = el;
                }}
              >
                <ChatCard
                  isTeamMemberChat={isTeamMemberChat}
                  sendername={reactionDisplayNames.sendername}
                  clientname={reactionDisplayNames.clientname}
                  time={getMessageTimestamp(msg)}
                  message={msg.message || msg.body}
                  messageType="facebook_reaction"
                  status={msg.status}
                  onReplyClick={handleScrollToMessage}
                  reaction={msg.reaction}
                  reactionPreview={msg.reactionPreview}
                  pillGrouping
                  isGroupStart={isFbReactionGroupStart}
                  isGroupEnd={isFbReactionGroupEnd}
                />
              </Box>
            );
          } else if (msg.type === "text") {
            // Handle text messages (from socket)
            // Use index to ensure unique keys even if message IDs are duplicated
            const uniqueKey = `text-${index}-${msg.id || msg._id || msg.tempId || 'no-id'}`;
            const textDisplayNames = getDisplayNames(msg);
            const isTextPlainPillType = !msg.messageType || ["text", "chat"].includes(msg.messageType);
            const isTextGroupStart = !isSamePillGroup(groupedChats[index - 1], msg);
            const isTextGroupEnd = !isSamePillGroup(msg, groupedChats[index + 1]);
            return (
              <Box
                sx={{ maxWidth: "100%" }}
                key={uniqueKey}
                ref={(el) => {
                  messageRefs.current[msg.id] = el;
                }}
              >
                <ChatCard
                  isTeamMemberChat={isTeamMemberChat}
                  sendername={textDisplayNames.sendername}
                  clientname={textDisplayNames.clientname}
                  time={getMessageTimestamp(msg)}
                  message={msg.message || msg.body}
                  messageType={msg.messageType || "text"}
                  replyBody={msg.replyBody}
                  replyType={msg.replyType}
                  replyId={msg.replyId}
                  status={msg.status}
                  onReplyClick={handleScrollToMessage}
                  replySenderName={msg.replySenderName}
                  reaction={msg.reaction}
                  isInternalMention={msg.isInternalMention}
                  senderAvatarColor={msg.senderAvatarColor}
                  audioDurationSeconds={msg.audioDurationSeconds}
                  pillGrouping={isTextPlainPillType}
                  messagePlatform={getMessageBadgePlatform(msg, messages?.platform)}
                  isGroupStart={isTextGroupStart}
                  isGroupEnd={isTextGroupEnd}
                />
              </Box>
            );
          } else if (msg.type === "template") {
            // Handle template messages
            // Use index to ensure unique keys even if message IDs are duplicated
            const uniqueKey = `template-${index}-${msg.id || msg._id || msg.tempId || 'no-id'}`;
            const templateDisplayNames = getDisplayNames(msg);
            const isTemplateGroupStart = !isSamePillGroup(groupedChats[index - 1], msg);
            const isTemplateGroupEnd = !isSamePillGroup(msg, groupedChats[index + 1]);
            return (
              <Box
                sx={{ maxWidth: "100%" }}
                key={uniqueKey}
                ref={(el) => {
                  messageRefs.current[msg.id] = el;
                }}
              >
                <ChatCard
                  isTeamMemberChat={isTeamMemberChat}
                  sendername={templateDisplayNames.sendername}
                  clientname={templateDisplayNames.clientname}
                  time={getMessageTimestamp(msg)}
                  message={msg.body || msg.message}
                  messageType="template"
                  status={msg.status}
                  onReplyClick={handleScrollToMessage}
                  reaction={msg.reaction}
                  isInternalMention={msg.isInternalMention}
                  senderAvatarColor={msg.senderAvatarColor}
                  messagePlatform={getMessageBadgePlatform(msg, messages?.platform)}
                  pillGrouping
                  isGroupStart={isTemplateGroupStart}
                  isGroupEnd={isTemplateGroupEnd}
                />
              </Box>
            );
          } else if (msg.type === "interactive" || (msg.type === "chat" && msg.messageType === "interactive")) {
            // Handle interactive messages (button messages) - same as regular chat messages
            // ChatCard handles alignment internally based on sendername/clientname
            // Use index to ensure unique keys even if message IDs are duplicated
            const uniqueKey = `interactive-${index}-${msg.id || msg._id || msg.tempId || 'no-id'}`;
            const messageContent = (msg.message && msg.message.trim()) || (msg.body && msg.body.trim()) || "";
            const interactiveDisplayNames = getDisplayNames(msg);
            const isInteractiveGroupStart = !isSamePillGroup(groupedChats[index - 1], msg);
            const isInteractiveGroupEnd = !isSamePillGroup(msg, groupedChats[index + 1]);
            return (
              <Box
                sx={{ maxWidth: "100%" }}
                key={uniqueKey}
                ref={(el) => {
                  if (msg.id || msg._id || msg.tempId) {
                    messageRefs.current[msg.id || msg._id || msg.tempId] = el;
                  }
                }}
              >
                <ChatCard
                  isTeamMemberChat={isTeamMemberChat}
                  sendername={interactiveDisplayNames.sendername}
                  clientname={interactiveDisplayNames.clientname}
                  time={getMessageTimestamp(msg)}
                  message={messageContent}
                  messageType="interactive"
                  replyBody={msg.replyBody}
                  replyType={msg.replyType}
                  replyId={msg.replyId}
                  status={msg.status}
                  onReplyClick={handleScrollToMessage}
                  replySenderName={msg.replySenderName}
                  reaction={msg.reaction}
                  isInternalMention={msg.isInternalMention}
                  senderAvatarColor={msg.senderAvatarColor}
                  pillGrouping
                  isGroupStart={isInteractiveGroupStart}
                  messagePlatform={getMessageBadgePlatform(msg, messages?.platform)}
                  isGroupEnd={isInteractiveGroupEnd}
                />
              </Box>
            );
          }
          else {
            return null;
          }
        })}
      </Box>
    );
  };

  export default ChatBody;
